import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInventory } from '../contexts/InventoryContext';
import { useAuth } from '../contexts/AuthContext';
import { FileText, Download, TrendingUp, DollarSign, ChartBar as BarChart3, Receipt, Package, Calendar, Filter, Activity, FileSpreadsheet, File, Clock, User, Users, ArrowUpDown, ChartPie as PieChart, CreditCard as Edit2, Trash2, MapPin, Save, X, Printer, Search, Check, Circle as XCircle, Ban, TriangleAlert as AlertTriangle, LogIn, LogOut, PackagePlus, PackageMinus, FileX, CalendarCheck, RefreshCw, ShoppingCart, Truck, Target, Zap, Pencil, RotateCcw, Store, ClipboardPen } from 'lucide-react';
import { ReservationsReport } from '../components/reports/ReservationsReport';
import { ShopReceiptsReport } from '../components/reports/ShopReceiptsReport';
import { ShopStockQtyReport } from '../components/reports/ShopStockQtyReport';
import { ReturnsReport } from '../components/reports/ReturnsReport';
import { ProductMatrixTable, prepareMatrixExportData } from '../components/reports/ProductMatrixTable';
import { LocationViewSelector, type LocationViewMode } from '../components/reports/LocationViewSelector';
import { LocationComparisonTable, prepareComparisonExportData } from '../components/reports/LocationComparisonTable';
import { OverviewMetricCard } from '../components/reports/OverviewMetricCard';
import { TimeRangeSelector, TIME_RANGES, type TimeRange } from '../components/reports/TimeRangeSelector';
import { OverviewLineChart } from '../components/reports/OverviewLineChart';
import { OverviewPieChart } from '../components/reports/OverviewPieChart';
import { OverviewBarChart } from '../components/reports/OverviewBarChart';
import { PerformanceMetrics } from '../components/reports/PerformanceMetrics';
import { Button } from '../components/ui/Button';
import { supabase, isAbortError } from '../lib/supabase';
import { getValidAuthHeadersOrThrow } from '../lib/auth';
import { fetchAllPaginated } from '../lib/fetchAllPaginated';
import { useLanguage } from '../contexts/LanguageContext';
import { detectCarrier, getCarrierColor } from '../utils/carrierDetection';
import { getAdminId as resolveAdminId } from '../utils/getAdminId';
import {
  fetchInventoryWithLocationBreakdown,
  fetchShopifyLocationMappings,
  calculateComparisonStats,
  type ProductWithLocationQuantities
} from '../utils/locationInventory';
import {
  exportOverviewToCSV,
  exportShipmentsToCSV,
  exportActivityLogToCSV,
  exportInvoicesToCSV,
  exportInventoryMatrixToExcel,
  exportOverviewToPDF,
  exportShipmentsToPDF,
  exportActivityLogToPDF,
  exportInvoicesToPDF,
  exportInventoryMatrixToPDF,
  exportLocationComparisonToExcel,
  exportLocationComparisonToPDF
} from '../utils/reportExport';
import { MissingOrderAnalysisReport } from '../components/reports/MissingOrderAnalysisReport';
import { getProductsBinLocations } from '../lib/services/binInventoryService';
import type { ProductBinLocation } from '../types/warehouse';
import { useOverviewAnalytics } from '../hooks/useOverviewAnalytics';
import { useEnterpriseAnalytics } from '../hooks/useEnterpriseAnalytics';
import { useWorkerFulfillmentStats } from '../hooks/useWorkerFulfillmentStats';
import { WorkerFulfillmentSection } from '../components/reports/WorkerFulfillmentSection';
import { ProfitAnalysis } from '../components/analytics/ProfitAnalysis';
import { ProductPerformance } from '../components/analytics/ProductPerformance';
import { ForecastingChart } from '../components/analytics/ForecastingChart';
import {
  exportAnalyticsToExcel,
  exportAnalyticsToPDF,
  exportAnalyticsOverviewToExcel,
  exportAnalyticsOverviewToPDF,
  exportAnalyticsProfitToExcel,
  exportAnalyticsProfitToPDF,
  exportAnalyticsProductsToExcel,
  exportAnalyticsProductsToPDF,
  exportAnalyticsForecastingToExcel,
  exportAnalyticsForecastingToPDF,
} from '../utils/analyticsExport';
import { Loader2 } from 'lucide-react';

interface SaveProgress {
  phase: 'idle' | 'saving' | 'pushing'
  current: number
  total: number
}

interface SaveFailedItem {
  productId: string
  name: string
  sku: string | null
  reason: string
}

interface SaveResult {
  status: 'success' | 'warning' | 'error'
  savedCount: number
  pushedCount: number
  mismatchCount: number
  skippedCount: number
  failedCount: number
  pushedToShopify: boolean
  shopifyLocationName?: string | null
  canceled?: boolean
  message: string
  failedItems: SaveFailedItem[]
}

const FAILED_ITEMS_DISPLAY_CAP = 50

function isTransientHttpStatus(status: number): boolean {
  return status === 502 || status === 503 || status === 429
}

async function upsertPliBatchWithRetry(
  rows: Array<{
    product_id: string
    location_id: string
    admin_id: string
    quantity: number
    updated_at: string
  }>,
  maxAttempts = 3
): Promise<{ ok: boolean; error?: string }> {
  let lastError = ''
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const { error } = await supabase
      .from('product_location_inventory')
      .upsert(rows, { onConflict: 'product_id,location_id' })
    if (!error) return { ok: true }
    lastError = error.message
    if (attempt < maxAttempts - 1) {
      await new Promise((r) => setTimeout(r, 500 * Math.pow(2, attempt)))
    }
  }
  return { ok: false, error: lastError }
}

export const Reports: React.FC = () => {
  const navigate = useNavigate();
  const { isOffline, user } = useAuth();
  const { items, shipments } = useInventory();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');

  // Set default date range to last month
  const getDefaultDateRange = () => {
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 1);

    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  };

  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [activityLogs, setActivityLogs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [detailedShipments, setDetailedShipments] = useState([]);
  const [editingShipment, setEditingShipment] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [loadingShipments, setLoadingShipments] = useState(false);
  const [shipmentFilter, setShipmentFilter] = useState<'all' | 'incoming' | 'outgoing'>('all');
  const [invoiceSearchTerm, setInvoiceSearchTerm] = useState('');
  const [shipmentSearchTerm, setShipmentSearchTerm] = useState('');
  const [inventorySearchTerm, setInventorySearchTerm] = useState('');
  const [colors, setColors] = useState<{id: string; name: string; hex_code?: string}[]>([]);
  const [sizes, setSizes] = useState<{id: string; name: string; sort_order?: number}[]>([]);
  const [inventorySortField, setInventorySortField] = useState<string>('name');
  const [inventorySortDirection, setInventorySortDirection] = useState<'asc' | 'desc'>('asc');
  const [processingInvoice, setProcessingInvoice] = useState<string | null>(null);
  const [showCancelModal, setShowCancelModal] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [showPrintModal, setShowPrintModal] = useState<any>(null);
  const [reportItems, setReportItems] = useState<any[]>([]);
  const [reportItemsLoading, setReportItemsLoading] = useState(false);
  const [loadedCount, setLoadedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);

  const [locationViewMode, setLocationViewMode] = useState<LocationViewMode>('warehouse');
  const [locations, setLocations] = useState<{ id: string; name: string }[]>([]);
  const [warehouseLocation, setWarehouseLocation] = useState<{ id: string; name: string } | null>(null);
  const [shopLocation, setShopLocation] = useState<{ id: string; name: string } | null>(null);
  const [warehouseShopifyName, setWarehouseShopifyName] = useState<string | null>(null);
  const [shopShopifyName, setShopShopifyName] = useState<string | null>(null);
  const [locationProducts, setLocationProducts] = useState<ProductWithLocationQuantities[]>([]);
  const [locationDataLoading, setLocationDataLoading] = useState(false);
  const [productLocationMap, setProductLocationMap] = useState<Map<string, Record<string, number>>>(new Map());
  const [binLocationMap, setBinLocationMap] = useState<Map<string, ProductBinLocation>>(new Map());

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const [isEditMode, setIsEditMode] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<Map<string, number>>(new Map());
  const [isSaving, setIsSaving] = useState(false);
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  // Inventory Matrix save progress + result feedback (enterprise: no silent failures)
  const [saveProgress, setSaveProgress] = useState<SaveProgress>({ phase: 'idle', current: 0, total: 0 });
  const [saveResult, setSaveResult] = useState<SaveResult | null>(null);
  const saveAbortRef = useRef<AbortController | null>(null);

  // Overview analytics state
  const [overviewTimeRange, setOverviewTimeRange] = useState<TimeRange>(TIME_RANGES[2]); // Default to "This Month"
  const [comparisonEnabled, setComparisonEnabled] = useState(false);
  const [refreshingOverview, setRefreshingOverview] = useState(false);
  
  // Analytics tab state
  const [analyticsActiveTab, setAnalyticsActiveTab] = useState<'overview' | 'profit' | 'products' | 'forecasting'>('overview');
  const [analyticsTimeRange, setAnalyticsTimeRange] = useState<TimeRange>(TIME_RANGES[2]);
  const [refreshingAnalytics, setRefreshingAnalytics] = useState(false);

  // Worker Fulfillment tab: preset range (1h / 1d / 7d) and end timestamp so range is fixed when selected
  const [workerFulfillmentRange, setWorkerFulfillmentRange] = useState<'1h' | '1d' | '7d'>('7d');
  const [workerFulfillmentRangeEnd, setWorkerFulfillmentRangeEnd] = useState(() => Date.now());

  const workerFulfillmentDateRange = useMemo(() => {
    const end = new Date(workerFulfillmentRangeEnd);
    const start = new Date(workerFulfillmentRangeEnd);
    if (workerFulfillmentRange === '1h') start.setHours(start.getHours() - 1);
    else if (workerFulfillmentRange === '1d') start.setDate(start.getDate() - 1);
    else start.setDate(start.getDate() - 7);
    return { start: start.toISOString(), end: end.toISOString() };
  }, [workerFulfillmentRange, workerFulfillmentRangeEnd]);

  const getAdminId = () => {
    if (!user) return null;
    return resolveAdminId(user);
  };

  const adminId = getAdminId();
  
  // Use overview analytics hook
  const overviewAnalytics = useOverviewAnalytics(adminId, overviewTimeRange, comparisonEnabled);
  
  // Use enterprise analytics hook (only when analytics tab is active)
  const enterpriseAnalytics = useEnterpriseAnalytics(
    activeTab === 'analytics' ? adminId : null,
    analyticsTimeRange
  );

  const workerFulfillmentStats = useWorkerFulfillmentStats(
    adminId,
    activeTab === 'worker_fulfillment' ? workerFulfillmentDateRange : null
  );

  const setWorkerFulfillmentPreset = useCallback((preset: '1h' | '1d' | '7d') => {
    setWorkerFulfillmentRange(preset);
    setWorkerFulfillmentRangeEnd(Date.now());
  }, []);

  const handleOverviewCustomRangeChange = useCallback((start: string, end: string) => {
    setOverviewTimeRange({ ...TIME_RANGES[0], value: 'custom', days: 0, customStart: start, customEnd: end, label: `${start} to ${end}` });
  }, []);

  const handleAnalyticsCustomRangeChange = useCallback((start: string, end: string) => {
    setAnalyticsTimeRange({ ...TIME_RANGES[0], value: 'custom', days: 0, customStart: start, customEnd: end, label: `${start} to ${end}` });
  }, []);
  
  const handleRefreshOverview = useCallback(async () => {
    setRefreshingOverview(true);
    overviewAnalytics.refetch();
    setTimeout(() => setRefreshingOverview(false), 1000);
  }, [overviewAnalytics.refetch]);

  const handleRefreshAnalytics = useCallback(async () => {
    setRefreshingAnalytics(true);
    enterpriseAnalytics.refetch();
    overviewAnalytics.refetch();
    setTimeout(() => setRefreshingAnalytics(false), 1000);
  }, [enterpriseAnalytics.refetch, overviewAnalytics.refetch]);

  const fetchAllInventoryForReport = useCallback(async () => {
    if (!user) return;

    setReportItemsLoading(true);
    setLoadedCount(0);
    setTotalCount(0);

    try {
      const adminId = getAdminId();
      if (!adminId) return;

      const { count, error: countError } = await supabase
        .from('products')
        .select('*', { count: 'exact', head: true })
        .eq('admin_id', adminId);

      if (countError) {
        console.error('Error fetching count:', countError);
        return;
      }

      const total = count || 0;
      setTotalCount(total);

      if (total === 0) {
        setReportItems([]);
        setLastUpdatedAt(new Date());
        return;
      }

      const chunkSize = 1000;
      const allItems: any[] = [];

      for (let offset = 0; offset < total; offset += chunkSize) {
        const { data, error } = await supabase
          .from('products')
          .select('id, name, sku, image_url, reference_code, color_id, size_id, quantity, purchase_price, cost_price, sell_price, unit_price, location, category, min_stock_level')
          .eq('admin_id', adminId)
          .order('name', { ascending: true })
          .range(offset, offset + chunkSize - 1);

        if (error) {
          console.error('Error fetching report items chunk:', error);
          break;
        }

        const mappedData = (data || []).map(item => ({
          ...item,
          buy_price: item.purchase_price ?? item.cost_price ?? 0,
          sell_price: item.sell_price ?? item.unit_price ?? 0
        }));

        allItems.push(...mappedData);
        setLoadedCount(allItems.length);
        setReportItems([...allItems]);
      }

      const productIds = allItems.map((item: any) => item.id).filter(Boolean);
      if (productIds.length > 0) {
        const binMap = await getProductsBinLocations(productIds, adminId);
        setBinLocationMap(binMap);
      }

      setLastUpdatedAt(new Date());
    } catch (err) {
      console.error('Error in fetchAllInventoryForReport:', err);
    } finally {
      setReportItemsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user && reportItems.length === 0 && !reportItemsLoading) {
      fetchAllInventoryForReport();
    }
  }, [user, fetchAllInventoryForReport]);

  const fetchLocationData = useCallback(async () => {
    const adminId = getAdminId();
    if (!adminId) return;

    setLocationDataLoading(true);
    try {
      const [data, shopifyMappings] = await Promise.all([
        fetchInventoryWithLocationBreakdown(adminId),
        fetchShopifyLocationMappings(adminId),
      ]);

      setLocations(data.locations);
      setWarehouseLocation(data.warehouseLocation);

      // Find shop location
      const shop = data.locations.find(l =>
        l.name.toLowerCase() === 'shop' ||
        l.name.toLowerCase().includes('shop') ||
        l.name.toLowerCase().includes('store')
      );
      setShopLocation(shop || null);

      // Resolve Shopify location names for warehouse and shop
      if (data.warehouseLocation) {
        const warehouseMapping = shopifyMappings.find(
          m => m.product_location_id === data.warehouseLocation!.id
        );
        setWarehouseShopifyName(warehouseMapping?.shopify_location_name || null);
      } else {
        setWarehouseShopifyName(null);
      }

      if (shop) {
        const shopMapping = shopifyMappings.find(
          m => m.product_location_id === shop.id
        );
        setShopShopifyName(shopMapping?.shopify_location_name || null);
      } else {
        setShopShopifyName(null);
      }

      setLocationProducts(data.products);

      // Create a map of product ID to location quantities for quick filtering
      const locationMap = new Map<string, Record<string, number>>();
      data.products.forEach(product => {
        locationMap.set(product.id, product.locationQuantities);
      });
      setProductLocationMap(locationMap);
    } catch (error) {
      console.error('Error fetching location data:', error);
    } finally {
      setLocationDataLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user && activeTab === 'inventory' && locations.length === 0 && !locationDataLoading) {
      fetchLocationData();
    }
  }, [user, activeTab, fetchLocationData]);

  const comparisonStats = useMemo(() => {
    if (!warehouseLocation || !shopLocation || locationViewMode !== 'difference') {
      return {
        totalProducts: 0,
        productsWithDifference: 0,
        warehouseOnlyCount: 0,
        otherLocationOnlyCount: 0,
        warehouseTotalQty: 0,
        otherLocationTotalQty: 0,
        netDifference: 0
      };
    }
    return calculateComparisonStats(locationProducts, warehouseLocation.id, shopLocation.id);
  }, [locationProducts, warehouseLocation, shopLocation, locationViewMode]);

  // Get target location ID for location-specific quantity display
  const targetLocationId = useMemo(() => {
    if (locationViewMode === 'shop') {
      return shopLocation?.id || null;
    }
    if (locationViewMode === 'warehouse') {
      return warehouseLocation?.id || null;
    }
    return null;
  }, [locationViewMode, shopLocation, warehouseLocation]);

  // Whether the currently selected single location is mapped to a Shopify
  // location. Edits to a mapped location must be pushed to Shopify so the
  // value sticks (otherwise the inbound inventory webhook overwrites it).
  const targetLocationIsShopifyMapped = useMemo(() => {
    if (locationViewMode === 'shop') return !!shopShopifyName;
    if (locationViewMode === 'warehouse') return !!warehouseShopifyName;
    return false;
  }, [locationViewMode, shopShopifyName, warehouseShopifyName]);

  // product_id -> set of location_ids that actually have a PLI record.
  const productLocationPresence = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const p of locationProducts) {
      map.set(p.id, new Set(p.locationIdsWithRecord));
    }
    return map;
  }, [locationProducts]);

  // Single-location views (Shop / Warehouse) show only products that actually
  // have an inventory record at the selected location. The Difference view
  // keeps every product so the comparison stays complete.
  const filteredReportItems = useMemo(() => {
    if (locationViewMode === 'difference' || !targetLocationId) return reportItems;
    // Avoid wrong totals while location breakdown is still loading.
    if (locationProducts.length === 0) return [];
    return reportItems.filter(item => {
      const locs = productLocationPresence.get(item.id);
      return locs ? locs.has(targetLocationId) : false;
    });
  }, [reportItems, locationViewMode, targetLocationId, locationProducts.length, productLocationPresence]);

  const handleQuantityChange = useCallback((itemId: string, newQuantity: number) => {
    setPendingChanges(prev => {
      const next = new Map(prev);
      next.set(itemId, Math.max(0, newQuantity));
      return next;
    });
  }, []);

  const handleCancelInventoryEdit = useCallback(() => {
    if (pendingChanges.size > 0) {
      if (!window.confirm(`You have ${pendingChanges.size} unsaved changes. Discard them?`)) return;
    }
    setPendingChanges(new Map());
    setSaveResult(null);
    setIsEditMode(false);
  }, [pendingChanges]);

  const handleCancelSave = useCallback(() => {
    saveAbortRef.current?.abort();
  }, []);

  const handleSaveChanges = useCallback(async () => {
    if (pendingChanges.size === 0 || !targetLocationId) return;
    const adminId = getAdminId();
    if (!adminId) return;

    const locationId = targetLocationId;
    const mustPush = targetLocationIsShopifyMapped;
    const shopifyLocationName =
      locationViewMode === 'shop'
        ? shopShopifyName
        : locationViewMode === 'warehouse'
          ? warehouseShopifyName
          : null;

    // Snapshot edits up-front so concurrent UI changes do not corrupt the run.
    const entries = Array.from(pendingChanges.entries()); // [productId, quantity][]
    const metaById = new Map<string, { name: string; sku: string | null }>();
    for (const it of reportItems) metaById.set(it.id, { name: it.name, sku: it.sku ?? null });
    const metaFor = (id: string) => metaById.get(id) || { name: id, sku: null };

    const failedItems: SaveFailedItem[] = [];
    const succeededLocally: string[] = [];
    const clearedFromPending = new Set<string>();
    let pushedCount = 0;
    let mismatchCount = 0;
    let skippedCount = 0;
    let canceled = false;

    setSaveResult(null);
    setIsSaving(true);
    saveAbortRef.current = new AbortController();
    const signal = saveAbortRef.current.signal;

    try {
      // ---- Phase 1: persist to product_location_inventory (source of truth)
      // products.quantity is recomputed by trigger_update_product_total_quantity on PLI.
      setSaveProgress({ phase: 'saving', current: 0, total: entries.length });
      const UPSERT_BATCH = 200;
      for (let i = 0; i < entries.length; i += UPSERT_BATCH) {
        if (signal.aborted) throw new DOMException('aborted', 'AbortError');
        const batch = entries.slice(i, i + UPSERT_BATCH);
        const rows = batch.map(([productId, quantity]) => ({
          product_id: productId,
          location_id: locationId,
          admin_id: adminId,
          quantity,
          updated_at: new Date().toISOString(),
        }));

        const upsertResult = await upsertPliBatchWithRetry(rows, 3);
        if (!upsertResult.ok) {
          console.error('PLI upsert error:', upsertResult.error);
          for (const [productId] of batch) {
            const meta = metaFor(productId);
            failedItems.push({
              productId,
              name: meta.name,
              sku: meta.sku,
              reason: `Save failed: ${upsertResult.error || 'unknown error'}`,
            });
          }
        } else {
          for (const [productId] of batch) succeededLocally.push(productId);
        }
        setSaveProgress({
          phase: 'saving',
          current: Math.min(i + batch.length, entries.length),
          total: entries.length,
        });
      }

      // ---- Phase 2: push to Shopify (mapped single location only)
      if (!mustPush) {
        for (const id of succeededLocally) clearedFromPending.add(id);
      } else if (succeededLocally.length > 0) {
        const PUSH_CHUNK = 40;
        const baseUrl = import.meta.env.VITE_SUPABASE_URL;
        const totalToPush = succeededLocally.length;
        let pushedSoFar = 0;
        setSaveProgress({ phase: 'pushing', current: 0, total: totalToPush });

        for (let i = 0; i < succeededLocally.length; i += PUSH_CHUNK) {
          if (signal.aborted) { canceled = true; break; }
          const chunk = succeededLocally.slice(i, i + PUSH_CHUNK);

          let data: {
            results?: Array<{ productId: string; status: string; error?: string }>
            remaining?: string[]
            failedIds?: string[]
          } | null = null;
          let lastErr: unknown = null;

          for (let attempt = 0; attempt < 2; attempt++) {
            try {
              const res = await fetch(`${baseUrl}/functions/v1/shopify-push-inventory/push-location`, {
                method: 'POST',
                headers: await getValidAuthHeadersOrThrow(),
                body: JSON.stringify({ productLocationId: locationId, productIds: chunk }),
                signal,
              });
              if (!res.ok) {
                const txt = await res.text();
                if (isTransientHttpStatus(res.status) && attempt === 0) {
                  await new Promise((r) => setTimeout(r, 1000));
                  continue;
                }
                throw new Error(`HTTP ${res.status}: ${txt.slice(0, 200)}`);
              }
              data = await res.json();
              lastErr = null;
              break;
            } catch (err) {
              if (isAbortError(err)) throw err;
              lastErr = err;
              if (attempt === 0) await new Promise((r) => setTimeout(r, 1000));
            }
          }

          if (!data) {
            for (const id of chunk) {
              const meta = metaFor(id);
              failedItems.push({
                productId: id,
                name: meta.name,
                sku: meta.sku,
                reason: lastErr instanceof Error ? lastErr.message : 'Push request failed (will revert, retry)',
              });
            }
            pushedSoFar += chunk.length;
            setSaveProgress({ phase: 'pushing', current: Math.min(pushedSoFar, totalToPush), total: totalToPush });
            continue;
          }

          const results = Array.isArray(data.results) ? data.results : [];
          const resultById = new Map(results.map((r) => [r.productId, r]));
          const remainingIds = new Set(Array.isArray(data.remaining) ? data.remaining : []);

          for (const id of chunk) {
            const r = resultById.get(id);
            const meta = metaFor(id);
            if (remainingIds.has(id) || !r) {
              failedItems.push({
                productId: id,
                name: meta.name,
                sku: meta.sku,
                reason: 'Not processed in time (will revert, retry)',
              });
            } else if (r.status === 'verified') {
              pushedCount++;
              clearedFromPending.add(id);
            } else if (r.status === 'mismatch') {
              mismatchCount++;
              failedItems.push({
                productId: id,
                name: meta.name,
                sku: meta.sku,
                reason: r.error || 'Verification mismatch (will revert, retry)',
              });
            } else if (r.status === 'skipped') {
              skippedCount++;
              clearedFromPending.add(id);
            } else {
              failedItems.push({
                productId: id,
                name: meta.name,
                sku: meta.sku,
                reason: r.error || 'Push failed (will revert, retry)',
              });
            }
          }

          pushedSoFar += chunk.length;
          setSaveProgress({ phase: 'pushing', current: Math.min(pushedSoFar, totalToPush), total: totalToPush });
        }
      }

      // ---- Keep only fully-succeeded items out of pending (no data loss)
      const remaining = new Map<string, number>();
      for (const [pid, qty] of entries) {
        if (!clearedFromPending.has(pid)) remaining.set(pid, qty);
      }
      setPendingChanges(remaining);
      if (remaining.size === 0) setIsEditMode(false);

      const dedupFailed = Array.from(
        new Map(failedItems.map((f) => [f.productId, f])).values()
      );
      const failedCount = remaining.size;
      const savedCount = succeededLocally.length;

      let status: SaveResult['status'] = 'success';
      if (savedCount === 0 && entries.length > 0) status = 'error';
      else if (failedCount > 0 || mismatchCount > 0) status = 'warning';

      let message = `Saved ${savedCount} locally.`;
      if (mustPush) {
        message += ` Pushed ${pushedCount}`;
        if (skippedCount > 0) message += `, skipped ${skippedCount} (no Shopify SKU)`;
        if (mismatchCount > 0) message += `, ${mismatchCount} mismatch`;
        if (failedCount > 0) message += `, ${failedCount} failed - kept for retry`;
        message += '.';
      } else if (failedCount > 0) {
        message += ` ${failedCount} failed - kept for retry.`;
      }
      if (canceled) message += ' Canceled.';

      setSaveResult({
        status,
        savedCount,
        pushedCount,
        mismatchCount,
        skippedCount,
        failedCount,
        pushedToShopify: mustPush,
        shopifyLocationName,
        canceled,
        message,
        failedItems: dedupFailed,
      });
    } catch (error) {
      if (isAbortError(error)) {
        // Items saved locally but not yet pushed remain in pendingChanges.
        const remaining = new Map<string, number>();
        for (const [pid, qty] of entries) {
          if (!clearedFromPending.has(pid)) remaining.set(pid, qty);
        }
        setPendingChanges(remaining);
        setSaveResult({
          status: 'warning',
          savedCount: succeededLocally.length,
          pushedCount,
          mismatchCount,
          skippedCount,
          failedCount: remaining.size,
          pushedToShopify: mustPush,
          shopifyLocationName,
          canceled: true,
          message: `Canceled. Saved ${succeededLocally.length}/${entries.length} locally; ${remaining.size} kept for retry.`,
          failedItems: Array.from(new Map(failedItems.map(f => [f.productId, f])).values()),
        });
      } else {
        console.error('Save error:', error);
        setSaveResult({
          status: 'error',
          savedCount: succeededLocally.length,
          pushedCount,
          mismatchCount,
          skippedCount,
          failedCount: entries.length - clearedFromPending.size,
          pushedToShopify: mustPush,
          shopifyLocationName,
          message: 'Failed to save changes. Please try again.',
          failedItems: Array.from(new Map(failedItems.map(f => [f.productId, f])).values()),
        });
      }
    } finally {
      setIsSaving(false);
      setSaveProgress({ phase: 'idle', current: 0, total: 0 });
      saveAbortRef.current = null;
      await fetchLocationData();
      await fetchAllInventoryForReport();
    }
  }, [
    pendingChanges,
    targetLocationId,
    targetLocationIsShopifyMapped,
    locationViewMode,
    shopShopifyName,
    warehouseShopifyName,
    reportItems,
    user,
    fetchLocationData,
    fetchAllInventoryForReport,
  ]);

  const handlePrintShipment = (shipment: any) => {
    setShowPrintModal(shipment);
  };

  const printShipmentDetails = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow || !showPrintModal) return;

    const shipment = showPrintModal;
    const isIncoming = shipment.type === 'incoming';

    const itemsHtml = shipment.shipment_items?.map((item: any) => {
      const colorName = getColorName(item.product?.color_id);
      const sizeName = getSizeName(item.product?.size_id);
      const qty = isIncoming ? (item.received_quantity || item.expected_quantity) : item.quantity;

      return `
        <tr>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb;">${item.product?.name || 'Unknown'}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb;">${item.product?.sku || '-'}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb;">${colorName || '-'}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb;">${sizeName || '-'}</td>
          <td style="padding: 12px; border-bottom: 1px solid #e5e7eb; text-align: right;">${qty}</td>
        </tr>
      `;
    }).join('') || '<tr><td colspan="5" style="padding: 12px; text-align: center;">No items</td></tr>';

    const totalQuantity = shipment.shipment_items?.reduce((sum: number, item: any) => {
      return sum + (isIncoming ? (item.received_quantity || item.expected_quantity || 0) : (item.quantity || 0));
    }, 0) || 0;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Shipment Details - ${shipment.shipment_number}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; color: #1f2937; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px; padding-bottom: 24px; border-bottom: 2px solid #e5e7eb; }
          .title { font-size: 28px; font-weight: 700; color: #111827; }
          .badge { display: inline-block; padding: 6px 16px; border-radius: 9999px; font-size: 12px; font-weight: 600; text-transform: uppercase; }
          .badge-incoming { background: #d1fae5; color: #065f46; }
          .badge-outgoing { background: #dbeafe; color: #1e40af; }
          .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 24px; margin-bottom: 32px; }
          .info-card { background: #f9fafb; border-radius: 12px; padding: 20px; }
          .info-label { font-size: 12px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px; }
          .info-value { font-size: 16px; font-weight: 600; color: #111827; }
          .table-container { border-radius: 12px; overflow: hidden; border: 1px solid #e5e7eb; }
          table { width: 100%; border-collapse: collapse; }
          th { background: #f3f4f6; padding: 14px 12px; text-align: left; font-size: 12px; font-weight: 600; color: #374151; text-transform: uppercase; letter-spacing: 0.05em; }
          th:last-child { text-align: right; }
          td { padding: 12px; }
          .total-row { background: #f9fafb; font-weight: 600; }
          .total-row td { padding: 14px 12px; }
          .footer { margin-top: 32px; padding-top: 24px; border-top: 1px solid #e5e7eb; text-align: center; color: #9ca3af; font-size: 12px; }
          .notes { margin-top: 24px; background: #fef3c7; border-radius: 12px; padding: 16px; }
          .notes-title { font-weight: 600; color: #92400e; margin-bottom: 8px; }
          .notes-content { color: #78350f; }
          @media print {
            body { padding: 20px; }
            .header { page-break-after: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="title">Shipment Details</div>
            <div style="margin-top: 8px; color: #6b7280;">${shipment.shipment_number}</div>
          </div>
          <span class="badge ${isIncoming ? 'badge-incoming' : 'badge-outgoing'}">
            ${isIncoming ? 'Incoming' : 'Outgoing'}
          </span>
        </div>

        <div class="info-grid">
          <div class="info-card">
            <div class="info-label">${isIncoming ? 'Supplier' : 'Customer'}</div>
            <div class="info-value">${shipment.customer_name || shipment.supplier_name || '-'}</div>
          </div>
          <div class="info-card">
            <div class="info-label">Date</div>
            <div class="info-value">${new Date(shipment.created_at).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
          </div>
          <div class="info-card">
            <div class="info-label">Status</div>
            <div class="info-value">${shipment.status || 'Completed'}</div>
          </div>
          ${!isIncoming && shipment.tracking_code ? `
          <div class="info-card">
            <div class="info-label">Tracking Code</div>
            <div class="info-value" style="font-family: monospace;">${shipment.tracking_code}${shipment.carrier ? ` (${shipment.carrier})` : ''}</div>
          </div>
          ` : `
          <div class="info-card">
            <div class="info-label">Total Items</div>
            <div class="info-value">${shipment.shipment_items?.length || 0} product(s)</div>
          </div>
          `}
        </div>

        ${!isIncoming && shipment.shipping_address ? `
        <div class="info-card" style="margin-bottom: 24px;">
          <div class="info-label">Shipping Address</div>
          <div class="info-value">${shipment.shipping_address}</div>
        </div>
        ` : ''}

        <div class="table-container">
          <table>
            <thead>
              <tr>
                <th>Product Name</th>
                <th>SKU</th>
                <th>Color</th>
                <th>Size</th>
                <th style="text-align: right;">Quantity</th>
              </tr>
            </thead>
            <tbody>
              ${itemsHtml}
              <tr class="total-row">
                <td colspan="4" style="text-align: right; font-weight: 600;">Total Quantity:</td>
                <td style="text-align: right; font-weight: 700;">${totalQuantity}</td>
              </tr>
            </tbody>
          </table>
        </div>

        ${shipment.notes ? `
        <div class="notes">
          <div class="notes-title">Notes</div>
          <div class="notes-content">${shipment.notes}</div>
        </div>
        ` : ''}

        <div class="footer">
          Printed on ${new Date().toLocaleString()} | ${user?.company_name || 'Inventory Management System'}
        </div>
      </body>
      </html>
    `);

    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  useEffect(() => {
    if (user) {
      fetchColorsAndSizes();
    }
  }, [user]);

  useEffect(() => {
    fetchActivityLogs();
    fetchDetailedShipments();
    fetchInvoices();
  }, [dateRange]);

  const fetchDetailedShipments = async () => {
    setLoadingShipments(true);
    try {
      const adminId = resolveAdminId(user);

      const startDate = new Date(dateRange.start);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(dateRange.end);
      endDate.setHours(23, 59, 59, 999);

      const [incomingResult, outgoingResult] = await Promise.all([
        supabase
          .from('incoming_shipments')
          .select(`
            *,
            shipment_items:shipment_items(
              id,
              expected_quantity,
              received_quantity,
              product:products(name, sku, color_id, size_id)
            )
          `)
          .eq('admin_id', adminId)
          .gte('created_at', startDate.toISOString())
          .lte('created_at', endDate.toISOString())
          .order('created_at', { ascending: false }),
        supabase
          .from('outgoing_shipments')
          .select(`
            *,
            shipment_items:outgoing_shipment_items(
              id,
              quantity,
              product:products(name, sku, color_id, size_id)
            )
          `)
          .eq('admin_id', adminId)
          .gte('created_at', startDate.toISOString())
          .lte('created_at', endDate.toISOString())
          .order('created_at', { ascending: false })
      ]);

      const incoming = (incomingResult.data || []).map(s => ({ ...s, type: 'incoming' }));
      const outgoing = (outgoingResult.data || []).map(s => ({ ...s, type: 'outgoing' }));

      const combined = [...incoming, ...outgoing].sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      setDetailedShipments(combined);
    } catch (error) {
      console.error('Error fetching detailed shipments:', error);
    } finally {
      setLoadingShipments(false);
    }
  };

  const fetchInvoices = async () => {
    try {
      const adminId = resolveAdminId(user);

      const startDate = new Date(dateRange.start);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(dateRange.end);
      endDate.setHours(23, 59, 59, 999);

      const { data, error } = await supabase
        .from('invoices')
        .select(`
          *,
          invoice_items:invoice_items(
            id,
            description,
            quantity,
            unit_price,
            total_price
          )
        `)
        .eq('admin_id', adminId)
        .gte('invoice_date', startDate.toISOString())
        .lte('invoice_date', endDate.toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;
      setInvoices(data || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  };

  const fetchColorsAndSizes = async () => {
    if (!user) return;

    try {
      const adminId = resolveAdminId(user);
      if (!adminId) {
        console.warn('No admin_id available for fetching colors/sizes');
        return;
      }

      const [colorsResult, sizesResult] = await Promise.all([
        supabase.from('product_colors').select('id, name, hex_code').eq('admin_id', adminId).order('name'),
        supabase.from('product_sizes').select('id, name, sort_order').eq('admin_id', adminId).order('sort_order', { ascending: true, nullsFirst: false })
      ]);
      setColors(colorsResult.data || []);
      setSizes(sizesResult.data || []);
    } catch (error) {
      console.error('Error fetching colors/sizes:', error);
    }
  };

  const fetchActivityLogs = async () => {
    try {
      const adminId = resolveAdminId(user);

      const startDate = new Date(dateRange.start);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(dateRange.end);
      endDate.setHours(23, 59, 59, 999);

      const { data, error } = await fetchAllPaginated((from, to) =>
        supabase
          .from('activity_logs')
          .select(`
          *,
          user:users!activity_logs_user_id_fkey(id, full_name, email)
        `)
          .eq('admin_id', adminId)
          .gte('created_at', startDate.toISOString())
          .lte('created_at', endDate.toISOString())
          .order('created_at', { ascending: false })
          .range(from, to)
      );

      if (error) throw error;
      setActivityLogs(data || []);
    } catch (error) {
      console.error('Error fetching activity logs:', error);
    }
  };

  const handleEditShipment = (shipment: any) => {
    setEditingShipment(shipment.id);
    setEditForm({
      tracking_code: shipment.tracking_code || '',
      notes: shipment.notes || '',
      status: shipment.status || ''
    });
  };

  const handleCancelEdit = () => {
    setEditingShipment(null);
    setEditForm({});
  };

  const handleSaveEdit = async (shipment: any) => {
    try {
      const table = shipment.type === 'incoming' ? 'incoming_shipments' : 'outgoing_shipments';
      const updates: any = {
        notes: (editForm as any).notes || null,
        status: (editForm as any).status || shipment.status
      };

      if (shipment.type === 'outgoing' && (editForm as any).tracking_code !== undefined) {
        const carrierInfo = (editForm as any).tracking_code ? detectCarrier((editForm as any).tracking_code) : null;
        updates.tracking_code = (editForm as any).tracking_code || null;
        updates.carrier = carrierInfo?.carrier || null;
        updates.tracking_status = (editForm as any).tracking_code ? (shipment.tracking_status || 'pending_pickup') : null;
      }

      const { error } = await supabase
        .from(table)
        .update(updates)
        .eq('id', shipment.id);

      if (error) throw error;

      alert('Shipment updated successfully!');
      setEditingShipment(null);
      setEditForm({});
      await fetchDetailedShipments();
    } catch (error) {
      console.error('Error updating shipment:', error);
      alert('Failed to update shipment');
    }
  };

  const handleDeleteShipment = async (shipment: any) => {
    if (!confirm(`Are you sure you want to delete this ${shipment.type} shipment?`)) {
      return;
    }

    try {
      const table = shipment.type === 'incoming' ? 'incoming_shipments' : 'outgoing_shipments';
      const { error } = await supabase
        .from(table)
        .delete()
        .eq('id', shipment.id);

      if (error) throw error;

      alert('Shipment deleted successfully!');
      await fetchDetailedShipments();
    } catch (error) {
      console.error('Error deleting shipment:', error);
      alert('Failed to delete shipment');
    }
  };

  const handleMarkAsPaid = async (invoice: any) => {
    if (invoice.payment_status === 'paid') return;
    setProcessingInvoice(invoice.id);
    try {
      const { error } = await supabase
        .from('invoices')
        .update({ payment_status: 'paid', paid_at: new Date().toISOString() })
        .eq('id', invoice.id);
      if (error) throw error;
      await supabase.from('activity_logs').insert({
        admin_id: resolveAdminId(user),
        user_id: user?.id,
        user_email: user?.email,
        action: 'Invoice Paid',
        details: `Invoice ${invoice.invoice_number} marked as paid. Amount: ${parseFloat(invoice.total_amount).toFixed(2)} EUR`
      });
      await fetchInvoices();
    } catch (error) {
      console.error('Error marking invoice as paid:', error);
      alert('Failed to mark invoice as paid');
    } finally {
      setProcessingInvoice(null);
    }
  };

  const handleCancelInvoice = async (invoice: any) => {
    if (invoice.payment_status === 'cancelled') return;
    setProcessingInvoice(invoice.id);
    try {
      const { data: invoiceItems } = await supabase
        .from('invoice_items')
        .select('product_id, quantity')
        .eq('invoice_id', invoice.id);
      if (invoiceItems && invoiceItems.length > 0) {
        for (const item of invoiceItems) {
          if (item.product_id) {
            const { data: product } = await supabase
              .from('products')
              .select('quantity')
              .eq('id', item.product_id)
              .maybeSingle();
            if (product) {
              await supabase
                .from('products')
                .update({ quantity: (product.quantity || 0) + item.quantity })
                .eq('id', item.product_id);
            }
          }
        }
      }
      await supabase
        .from('invoices')
        .update({
          payment_status: 'cancelled',
          cancelled_at: new Date().toISOString(),
          cancelled_by: user?.id,
          cancellation_reason: cancelReason || 'No reason provided'
        })
        .eq('id', invoice.id);
      await supabase.from('activity_logs').insert({
        admin_id: resolveAdminId(user),
        user_id: user?.id,
        action_type: 'invoice_cancelled',
        entity_type: 'invoice',
        entity_id: invoice.id,
        description: `${user?.full_name || user?.email} cancelled invoice ${invoice.invoice_number}`,
        metadata: {
          invoice_number: invoice.invoice_number,
          customer_name: invoice.customer_name,
          total_amount: parseFloat(invoice.total_amount),
          items_restored: invoiceItems?.length || 0,
          cancellation_reason: cancelReason || 'No reason provided'
        },
        created_at: new Date().toISOString()
      });
      setShowCancelModal(null);
      setCancelReason('');
      await fetchInvoices();
    } catch (error) {
      console.error('Error cancelling invoice:', error);
      alert('Failed to cancel invoice');
    } finally {
      setProcessingInvoice(null);
    }
  };

  const dataSource = reportItems.length > 0 ? reportItems : items;

  const stats = useMemo(() => ({
    totalItems: dataSource.length,
    totalQuantity: dataSource.reduce((sum, item) => sum + (item.quantity || 0), 0),
    lowStock: dataSource.filter(item => (item.quantity || 0) <= (item.min_stock_level || 10)).length,
    totalInventoryValue: dataSource.reduce((sum, item) => sum + ((item.buy_price || 0) * (item.quantity || 0)), 0),
    totalSellValue: dataSource.reduce((sum, item) => sum + ((item.sell_price || 0) * (item.quantity || 0)), 0),
    incomingShipments: detailedShipments.filter(s => s.type === 'incoming').length,
    outgoingShipments: detailedShipments.filter(s => s.type === 'outgoing').length,
  }), [dataSource, detailedShipments]);

  const categoryStats = useMemo(() => dataSource.reduce((acc: any, item) => {
    const category = item.category || 'Uncategorized';
    if (!acc[category]) {
      acc[category] = {
        count: 0,
        quantity: 0,
        buyValue: 0,
        sellValue: 0
      };
    }
    acc[category].count++;
    acc[category].quantity += (item.quantity || 0);
    acc[category].buyValue += (item.buy_price || 0) * (item.quantity || 0);
    acc[category].sellValue += (item.sell_price || 0) * (item.quantity || 0);
    return acc;
  }, {}), [dataSource]);

  const formatLastUpdated = useCallback((date: Date | null) => {
    if (!date) return null;
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;

    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  const activeInvoices = invoices.filter(inv => inv.payment_status !== 'cancelled');
  const invoiceStats = {
    totalInvoices: invoices.length,
    totalRevenue: invoices
      .filter(inv => inv.payment_status === 'paid')
      .reduce((sum, inv) => sum + parseFloat(inv.total_amount || 0), 0),
    pendingPayments: invoices.filter(inv => inv.payment_status === 'unpaid').length,
    cancelledInvoices: invoices.filter(inv => inv.payment_status === 'cancelled').length,
    avgInvoiceValue: activeInvoices.length > 0
      ? activeInvoices.reduce((sum, inv) => sum + parseFloat(inv.total_amount || 0), 0) / activeInvoices.length
      : 0
  };

  const exportToCSV = async () => {
    if (isOffline) {
      alert('Export requires internet connection');
      return;
    }

    const { data: settings } = await supabase
      .from('user_settings')
      .select('avatar_data')
      .eq('user_id', user?.id)
      .maybeSingle();

    const options = {
      companyName: user?.company_name || 'Inventory Management System',
      companyAddress: user?.company_address,
      companyPhone: user?.company_phone,
      companyEmail: user?.company_email,
      companyLogo: settings?.avatar_data,
      dateRange
    };

    switch(activeTab) {
      case 'overview':
        exportOverviewToCSV(dataSource, categoryStats, options);
        break;
      case 'inventory':
        const matrixData = prepareMatrixExportData(dataSource, colors, sizes);
        exportInventoryMatrixToExcel(matrixData, options);
        break;
      case 'shipments':
        exportShipmentsToCSV(detailedShipments, options);
        break;
      case 'activity':
        exportActivityLogToCSV(activityLogs, options);
        break;
      case 'invoices':
        exportInvoicesToCSV(invoices, options);
        break;
      default:
        exportOverviewToCSV(dataSource, categoryStats, options);
    }
  };

  const exportToPDF = async () => {
    if (isOffline) {
      alert('Export requires internet connection');
      return;
    }

    const { data: settings } = await supabase
      .from('user_settings')
      .select('avatar_data')
      .eq('user_id', user?.id)
      .maybeSingle();

    const options = {
      companyName: user?.company_name || 'Inventory Management System',
      companyAddress: user?.company_address,
      companyPhone: user?.company_phone,
      companyEmail: user?.company_email,
      companyLogo: settings?.avatar_data,
      dateRange
    };

    switch(activeTab) {
      case 'overview':
        exportOverviewToPDF(dataSource, categoryStats, options);
        break;
      case 'inventory':
        const matrixDataPdf = prepareMatrixExportData(dataSource, colors, sizes);
        exportInventoryMatrixToPDF(matrixDataPdf, options);
        break;
      case 'shipments':
        exportShipmentsToPDF(detailedShipments, options);
        break;
      case 'activity':
        exportActivityLogToPDF(activityLogs, options);
        break;
      case 'invoices':
        exportInvoicesToPDF(invoices, options);
        break;
      default:
        exportOverviewToPDF(dataSource, categoryStats, options);
    }
  };

  const tabs = [
    { id: 'overview', label: t('reports.tabs.overview'), icon: BarChart3 },
    { id: 'inventory', label: 'Inventory', icon: Package },
    { id: 'shipments', label: t('reports.tabs.shipments'), icon: Package },
    { id: 'activity', label: t('reports.tabs.activity'), icon: Activity },
    { id: 'worker_fulfillment', label: t('reports.tabs.workerFulfillment'), icon: Users },
    { id: 'invoices', label: t('reports.tabs.invoices'), icon: Receipt },
    { id: 'missing_order_analysis', label: 'Missing Order Analysis', icon: AlertTriangle },
    { id: 'reservations', label: 'Reservations', icon: CalendarCheck },
    { id: 'shop_receipts', label: 'Shop Submissions', icon: Store },
    { id: 'shop_stock_qty', label: 'Shop Stock Qty', icon: ClipboardPen },
    { id: 'returns', label: 'Returns Report', icon: RotateCcw },
    { id: 'analytics', label: 'Analytics', icon: Target }
  ];

  const getColorName = (colorId: string | undefined) => {
    if (!colorId) return undefined;
    return colors.find(c => c.id === colorId)?.name;
  };

  const getSizeName = (sizeId: string | undefined) => {
    if (!sizeId) return undefined;
    return sizes.find(s => s.id === sizeId)?.name;
  };

  const getInventoryReportItems = () => {
    return dataSource.map(item => ({
      name: item.name,
      reference_code: item.reference_code,
      color_name: getColorName(item.color_id),
      size_name: getSizeName(item.size_id),
      category: item.category,
      quantity: item.quantity,
      buy_price: item.buy_price,
      sell_price: item.sell_price,
      location: item.location
    }));
  };

  const filteredInventoryItems = useMemo(() => dataSource
    .filter(item => {
      if (!inventorySearchTerm) return true;
      const searchLower = inventorySearchTerm.toLowerCase();
      return (
        item.name?.toLowerCase().includes(searchLower) ||
        item.reference_code?.toLowerCase().includes(searchLower) ||
        item.category?.toLowerCase().includes(searchLower) ||
        item.location?.toLowerCase().includes(searchLower) ||
        getColorName(item.color_id)?.toLowerCase().includes(searchLower) ||
        getSizeName(item.size_id)?.toLowerCase().includes(searchLower)
      );
    })
    .sort((a, b) => {
      let aVal: any, bVal: any;
      switch (inventorySortField) {
        case 'name': aVal = a.name || ''; bVal = b.name || ''; break;
        case 'reference': aVal = a.reference_code || ''; bVal = b.reference_code || ''; break;
        case 'color': aVal = getColorName(a.color_id) || ''; bVal = getColorName(b.color_id) || ''; break;
        case 'size': aVal = getSizeName(a.size_id) || ''; bVal = getSizeName(b.size_id) || ''; break;
        case 'category': aVal = a.category || ''; bVal = b.category || ''; break;
        case 'quantity': aVal = a.quantity; bVal = b.quantity; break;
        case 'buy_price': aVal = a.buy_price || 0; bVal = b.buy_price || 0; break;
        case 'sell_price': aVal = a.sell_price || 0; bVal = b.sell_price || 0; break;
        case 'location': aVal = a.location || ''; bVal = b.location || ''; break;
        default: aVal = a.name || ''; bVal = b.name || '';
      }
      if (typeof aVal === 'string') {
        return inventorySortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return inventorySortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    }), [dataSource, inventorySearchTerm, inventorySortField, inventorySortDirection, colors, sizes]);

  const handleInventorySort = (field: string) => {
    if (inventorySortField === field) {
      setInventorySortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setInventorySortField(field);
      setInventorySortDirection('asc');
    }
  };

  return (
    <div className="p-6 w-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {t('reports.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {t('reports.subtitle')}
          </p>
          {lastUpdatedAt && !reportItemsLoading && (
            <p className="text-sm text-gray-500 dark:text-gray-500 mt-1 flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              Last updated: {formatLastUpdated(lastUpdatedAt)}
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-3 mt-4 sm:mt-0">
          {/* Date Range Picker */}
          <div className="flex gap-2 items-center bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-gray-800 dark:to-gray-700 rounded-xl px-5 py-3 border-2 border-blue-200 dark:border-gray-600 hover:border-blue-400 dark:hover:border-cyan-500 transition-all shadow-md hover:shadow-lg">
            <Calendar className="h-5 w-5 text-blue-600 dark:text-cyan-400 flex-shrink-0" />
            <div className="relative">
              <input
                type="date"
                value={dateRange.start}
                max={dateRange.end}
                onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                onClick={(e) => e.currentTarget.showPicker()}
                className="bg-transparent text-sm font-semibold outline-none text-gray-900 dark:text-gray-100 cursor-pointer hover:text-blue-600 dark:hover:text-cyan-400 transition-colors min-w-[110px] [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                style={{ colorScheme: 'dark' }}
              />
            </div>
            <span className="text-blue-400 dark:text-cyan-400 font-bold text-lg">-</span>
            <div className="relative">
              <input
                type="date"
                value={dateRange.end}
                min={dateRange.start}
                onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                onClick={(e) => e.currentTarget.showPicker()}
                className="bg-transparent text-sm font-semibold outline-none text-gray-900 dark:text-gray-100 cursor-pointer hover:text-blue-600 dark:hover:text-cyan-400 transition-colors min-w-[110px] [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                style={{ colorScheme: 'dark' }}
              />
            </div>
          </div>

          <Button
            onClick={fetchAllInventoryForReport}
            variant="secondary"
            disabled={reportItemsLoading}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-5 w-5 ${reportItemsLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={exportToCSV} variant="secondary" disabled={reportItemsLoading} className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            CSV
          </Button>
          <Button onClick={exportToPDF} variant="secondary" disabled={reportItemsLoading} className="flex items-center gap-2">
            <File className="h-5 w-5" />
            PDF
          </Button>
        </div>
      </div>

      {/* Progress Indicator */}
      {reportItemsLoading && (
        <div className="mb-6 bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Loading inventory data...
            </span>
            <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
              {loadedCount.toLocaleString()} / {totalCount.toLocaleString()} items
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden">
            <div
              className="bg-gradient-to-r from-blue-500 to-cyan-500 h-2.5 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${totalCount > 0 ? (loadedCount / totalCount) * 100 : 0}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            {totalCount > 0 ? Math.round((loadedCount / totalCount) * 100) : 0}% complete
          </p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-8 border-b border-gray-200 dark:border-gray-700">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors border-b-2 ${
              activeTab === tab.id
                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            <tab.icon className="h-5 w-5" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-8">
          {/* Header with Time Range Selector and Actions */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                <BarChart3 className="h-6 w-6 text-blue-600 mr-3" />
                Overview & Analytics
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Comprehensive insights into your inventory, sales, and operations
              </p>
            </div>
            <div className="flex items-center gap-3">
              <TimeRangeSelector
                selectedRange={overviewTimeRange}
                onRangeChange={setOverviewTimeRange}
                onCustomRangeChange={handleOverviewCustomRangeChange}
                customStartDate={overviewTimeRange.customStart}
                customEndDate={overviewTimeRange.customEnd}
                comparisonEnabled={comparisonEnabled}
                onComparisonToggle={setComparisonEnabled}
                showComparison={true}
              />
              <Button
                variant="secondary"
                onClick={handleRefreshOverview}
                disabled={overviewAnalytics.loading || refreshingOverview}
                className="flex items-center gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${refreshingOverview || overviewAnalytics.loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  // Use current items for export
                  const exportItems = reportItems.length > 0 ? reportItems : items;
                  const categoryStatsForExport = Object.entries(categoryStats).reduce((acc, [cat, stat]: [string, any]) => {
                    acc[cat] = {
                      count: stat.count,
                      quantity: stat.quantity,
                      buyValue: stat.buyValue,
                      sellValue: stat.sellValue
                    };
                    return acc;
                  }, {} as Record<string, any>);
                  
                  exportOverviewToCSV(
                    exportItems,
                    categoryStatsForExport,
                    {
                      companyName: user?.company_name || 'Inventory Management System',
                      companyAddress: user?.address,
                    }
                  );
                }}
                className="flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Export CSV
              </Button>
              <Button
                variant="secondary"
                onClick={async () => {
                  const exportItems = reportItems.length > 0 ? reportItems : items;
                  const categoryStatsForExport = Object.entries(categoryStats).reduce((acc, [cat, stat]: [string, any]) => {
                    acc[cat] = {
                      count: stat.count,
                      quantity: stat.quantity,
                      buyValue: stat.buyValue,
                      sellValue: stat.sellValue
                    };
                    return acc;
                  }, {} as Record<string, any>);
                  
                  const { data: settings } = await supabase
                    .from('user_settings')
                    .select('avatar_data')
                    .eq('user_id', user?.id)
                    .maybeSingle();
                  
                  exportOverviewToPDF(
                    exportItems,
                    categoryStatsForExport,
                    {
                      companyName: user?.company_name || 'Inventory Management System',
                      companyAddress: user?.address,
                      companyLogo: settings?.avatar_data
                    }
                  );
                }}
                className="flex items-center gap-2"
              >
                <Printer className="h-4 w-4" />
                Export PDF
              </Button>
            </div>
          </div>

          {overviewAnalytics.loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-4"></div>
              <p className="text-gray-500 dark:text-gray-400">Loading analytics...</p>
            </div>
          ) : overviewAnalytics.error ? (
            <div className="card p-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
                <p className="text-red-600 dark:text-red-400">{overviewAnalytics.error}</p>
              </div>
            </div>
          ) : (
            <>
              {/* Enhanced Metric Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <OverviewMetricCard
                  title={t('reports.stats.totalItems')}
                  value={overviewAnalytics.inventory.totalItems}
                  subtitle={`${overviewAnalytics.inventory.categories} categories`}
                  icon={Package}
                  trend={comparisonEnabled ? {
                    value: overviewAnalytics.inventory.trends.items.changePercent,
                    positive: overviewAnalytics.inventory.trends.items.isPositive,
                    label: 'vs previous period'
                  } : undefined}
                  gradient="from-blue-500 to-blue-600"
                  format="count"
                />
                <OverviewMetricCard
                  title={t('reports.stats.totalQuantity')}
                  value={overviewAnalytics.inventory.totalQuantity}
                  subtitle="Total units in stock"
                  icon={TrendingUp}
                  trend={comparisonEnabled ? {
                    value: overviewAnalytics.inventory.trends.quantity.changePercent,
                    positive: overviewAnalytics.inventory.trends.quantity.isPositive,
                    label: 'vs previous period'
                  } : undefined}
                  gradient="from-green-500 to-green-600"
                  format="count"
                />
                <OverviewMetricCard
                  title={t('reports.stats.totalValue')}
                  value={overviewAnalytics.inventory.totalValue}
                  subtitle="Inventory value"
                  icon={DollarSign}
                  trend={comparisonEnabled ? {
                    value: overviewAnalytics.inventory.trends.value.changePercent,
                    positive: overviewAnalytics.inventory.trends.value.isPositive,
                    label: 'vs previous period'
                  } : undefined}
                  gradient="from-indigo-500 to-indigo-600"
                  format="currency"
                />
                <OverviewMetricCard
                  title="Total Revenue"
                  value={overviewAnalytics.sales.totalRevenue}
                  subtitle={`${overviewAnalytics.sales.totalOrders} orders`}
                  icon={ShoppingCart}
                  trend={comparisonEnabled ? {
                    value: overviewAnalytics.sales.trends.revenue.changePercent,
                    positive: overviewAnalytics.sales.trends.revenue.isPositive,
                    label: 'vs previous period'
                  } : undefined}
                  gradient="from-purple-500 to-purple-600"
                  format="currency"
                />
              </div>

              {/* Additional Metrics Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <OverviewMetricCard
                  title={t('reports.stats.lowStock')}
                  value={overviewAnalytics.inventory.lowStockCount}
                  subtitle={`${overviewAnalytics.inventory.outOfStockCount} out of stock`}
                  icon={AlertTriangle}
                  gradient="from-yellow-500 to-yellow-600"
                  format="count"
                />
                <OverviewMetricCard
                  title="Average Order Value"
                  value={overviewAnalytics.sales.avgOrderValue}
                  subtitle="Per order"
                  icon={Receipt}
                  gradient="from-cyan-500 to-cyan-600"
                  format="currency"
                />
                <OverviewMetricCard
                  title="Incoming Shipments"
                  value={overviewAnalytics.shipments.incoming}
                  subtitle={`${overviewAnalytics.shipments.pending} pending`}
                  icon={PackagePlus}
                  trend={comparisonEnabled ? {
                    value: overviewAnalytics.shipments.trends.incoming.changePercent,
                    positive: overviewAnalytics.shipments.trends.incoming.isPositive,
                    label: 'vs previous period'
                  } : undefined}
                  gradient="from-green-500 to-emerald-600"
                  format="count"
                />
                <OverviewMetricCard
                  title="Outgoing Shipments"
                  value={overviewAnalytics.shipments.outgoing}
                  subtitle={`${overviewAnalytics.shipments.delivered} delivered`}
                  icon={PackageMinus}
                  trend={comparisonEnabled ? {
                    value: overviewAnalytics.shipments.trends.outgoing.changePercent,
                    positive: overviewAnalytics.shipments.trends.outgoing.isPositive,
                    label: 'vs previous period'
                  } : undefined}
                  gradient="from-orange-500 to-orange-600"
                  format="count"
                />
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <OverviewLineChart
                  data={overviewAnalytics.sales.revenueTrend.map(point => ({
                    date: point.date,
                    value: point.value,
                    label: point.label || point.date,
                  }))}
                  title="Revenue Trend"
                  color="purple"
                  height={300}
                />
                <OverviewPieChart
                  data={overviewAnalytics.inventory.topCategories.map((cat, index) => ({
                    label: cat.name,
                    value: cat.value,
                  }))}
                  title="Top Categories by Value"
                  height={300}
                />
              </div>

              {/* Performance Metrics */}
              <PerformanceMetrics
                metrics={[
                  {
                    label: 'Inventory Turnover',
                    value: overviewAnalytics.performance.inventoryTurnover,
                    target: 4,
                    unit: 'x',
                    description: 'Times per year',
                    color: 'text-blue-500',
                  },
                  {
                    label: 'Fulfillment Rate',
                    value: overviewAnalytics.performance.fulfillmentRate,
                    target: 95,
                    unit: '%',
                    description: 'Orders fulfilled',
                    color: 'text-green-500',
                  },
                  {
                    label: 'Order Growth',
                    value: comparisonEnabled ? overviewAnalytics.sales.trends.orders.changePercent : 0,
                    target: 10,
                    unit: '%',
                    description: 'Period over period',
                    color: overviewAnalytics.sales.trends.orders.isPositive ? 'text-green-500' : 'text-red-500',
                  },
                  {
                    label: 'Inventory Growth',
                    value: comparisonEnabled ? overviewAnalytics.inventory.trends.quantity.changePercent : 0,
                    target: 5,
                    unit: '%',
                    description: 'Period over period',
                    color: overviewAnalytics.inventory.trends.quantity.isPositive ? 'text-green-500' : 'text-red-500',
                  },
                ]}
                title="Performance Metrics"
              />

              {/* Category Breakdown - Enhanced */}
              <div className="card p-8">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-6 flex items-center">
                  <PieChart className="h-6 w-6 text-blue-600 mr-3" />
                  {t('reports.categoryBreakdown')}
                </h2>

                {overviewAnalytics.inventory.topCategories.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {overviewAnalytics.inventory.topCategories.map((category) => {
                      const percentage = overviewAnalytics.inventory.totalItems > 0
                        ? (category.count / overviewAnalytics.inventory.totalItems) * 100
                        : 0;
                      
                      return (
                        <div key={category.name} className="p-6 bg-gray-50 dark:bg-gray-800 rounded-xl hover:shadow-lg transition-shadow">
                          <div className="flex justify-between items-start mb-3">
                            <h3 className="font-semibold text-lg text-gray-900 dark:text-gray-100">
                              {category.name}
                            </h3>
                            <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                              {percentage.toFixed(1)}%
                            </span>
                          </div>
                          <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                            <div className="flex justify-between">
                              <span>{t('reports.items')}:</span>
                              <span className="font-medium">{category.count}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>{t('reports.value')}:</span>
                              <span className="font-medium text-blue-600 dark:text-blue-400">
                                €{category.value.toFixed(0)}
                              </span>
                            </div>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-4">
                            <div
                              className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                              style={{ width: `${Math.min(percentage, 100)}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No category data available
                  </div>
                )}
              </div>

              {/* Last Updated */}
              {overviewAnalytics.lastUpdated && (
                <div className="text-sm text-gray-500 dark:text-gray-400 text-center">
                  Last updated: {overviewAnalytics.lastUpdated.toLocaleString()}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Inventory Tab */}
      {activeTab === 'inventory' && (
        <div className="space-y-6">
          <div className="card p-4 sm:p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
              <div>
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                  <Package className="h-6 w-6 text-blue-600 mr-3" />
                  {locationViewMode === 'difference' ? 'Location Comparison Report' : 'Inventory Matrix Report'}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {locationViewMode === 'difference'
                    ? 'Compare inventory quantities between Warehouse and Shop'
                    : locationViewMode === 'shop'
                    ? `Showing inventory for ${shopLocation?.name || 'Shop'} location`
                    : locationViewMode === 'warehouse'
                    ? `Showing inventory for ${warehouseLocation?.name || 'Warehouse'} location`
                    : 'Product-centric view with size breakdown by color'
                  }
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                {locationViewMode === 'difference' && warehouseLocation && shopLocation ? (
                  <>
                    <Button
                      onClick={() => {
                        const exportData = prepareComparisonExportData(
                          locationProducts,
                          warehouseLocation,
                          shopLocation,
                          colors,
                          sizes,
                          comparisonStats
                        );
                        exportLocationComparisonToExcel(exportData, {
                          companyName: user?.company_name || 'Inventory Management System',
                          companyAddress: user?.company_address,
                          companyPhone: user?.company_phone,
                          companyEmail: user?.company_email
                        });
                      }}
                      variant="secondary"
                      className="flex items-center gap-2"
                      disabled={locationDataLoading}
                    >
                      <FileSpreadsheet className="h-4 w-4" />
                      Export Comparison
                    </Button>
                    <Button
                      onClick={async () => {
                        const { data: settings } = await supabase
                          .from('user_settings')
                          .select('avatar_data')
                          .eq('user_id', user?.id)
                          .maybeSingle();
                        const exportData = prepareComparisonExportData(
                          locationProducts,
                          warehouseLocation,
                          shopLocation,
                          colors,
                          sizes,
                          comparisonStats
                        );
                        exportLocationComparisonToPDF(exportData, {
                          companyName: user?.company_name || 'Inventory Management System',
                          companyAddress: user?.company_address,
                          companyPhone: user?.company_phone,
                          companyEmail: user?.company_email,
                          companyLogo: settings?.avatar_data
                        });
                      }}
                      variant="secondary"
                      className="flex items-center gap-2"
                      disabled={locationDataLoading}
                    >
                      <Printer className="h-4 w-4" />
                      Print Comparison
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      onClick={async () => {
                        setIsExportingExcel(true);
                        try {
                          const matrixData = prepareMatrixExportData(filteredReportItems, colors, sizes, binLocationMap);
                          await exportInventoryMatrixToExcel(matrixData, {
                            companyName: user?.company_name || 'Inventory Management System',
                            companyAddress: user?.company_address,
                            companyPhone: user?.company_phone,
                            companyEmail: user?.company_email
                          });
                        } catch (err) {
                          alert(`Export failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
                        } finally {
                          setIsExportingExcel(false);
                        }
                      }}
                      variant="secondary"
                      className="flex items-center gap-2"
                      loading={isExportingExcel}
                      disabled={reportItemsLoading || locationDataLoading || isEditMode || isExportingExcel}
                    >
                      <FileSpreadsheet className="h-4 w-4" />
                      Export to Excel
                    </Button>
                    <Button
                      onClick={async () => {
                        const { data: settings } = await supabase
                          .from('user_settings')
                          .select('avatar_data')
                          .eq('user_id', user?.id)
                          .maybeSingle();
                        const matrixData = prepareMatrixExportData(filteredReportItems, colors, sizes, binLocationMap);
                        exportInventoryMatrixToPDF(matrixData, {
                          companyName: user?.company_name || 'Inventory Management System',
                          companyAddress: user?.company_address,
                          companyPhone: user?.company_phone,
                          companyEmail: user?.company_email,
                          companyLogo: settings?.avatar_data
                        });
                      }}
                      variant="secondary"
                      className="flex items-center gap-2"
                      disabled={reportItemsLoading || locationDataLoading || isEditMode}
                    >
                      <Printer className="h-4 w-4" />
                      Print PDF
                    </Button>
                    {isAdmin && targetLocationId && (
                      <>
                        {isEditMode ? (
                          <>
                            <Button
                              onClick={handleSaveChanges}
                              variant="success"
                              className="flex items-center gap-2"
                              loading={isSaving}
                              disabled={pendingChanges.size === 0 || isSaving}
                            >
                              <Save className="h-4 w-4" />
                              Save{pendingChanges.size > 0 ? ` (${pendingChanges.size})` : ''}
                            </Button>
                            {isSaving ? (
                              <Button
                                onClick={handleCancelSave}
                                variant="danger"
                                className="flex items-center gap-2"
                              >
                                <Ban className="h-4 w-4" />
                                Stop
                              </Button>
                            ) : (
                              <Button
                                onClick={handleCancelInventoryEdit}
                                variant="danger"
                                className="flex items-center gap-2"
                              >
                                <X className="h-4 w-4" />
                                Cancel
                              </Button>
                            )}
                          </>
                        ) : (
                          <Button
                            onClick={() => setIsEditMode(true)}
                            variant="secondary"
                            className="flex items-center gap-2 border-2 border-transparent hover:border-amber-400 dark:hover:border-amber-500"
                            disabled={reportItemsLoading || locationDataLoading}
                          >
                            <Pencil className="h-4 w-4" />
                            Edit Quantities
                          </Button>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="mb-6">
              <LocationViewSelector
                viewMode={locationViewMode}
                onViewModeChange={(mode) => {
                  if (isEditMode) {
                    if (pendingChanges.size > 0) {
                      if (!window.confirm('You have unsaved changes. Discard and switch location?')) return;
                    }
                    setPendingChanges(new Map());
                    setIsEditMode(false);
                  }
                  setSaveResult(null);
                  setLocationViewMode(mode);
                }}
                shopLocationId={shopLocation?.id || null}
                warehouseLocationId={warehouseLocation?.id || null}
                loading={locationDataLoading || reportItemsLoading || isSaving}
                warehouseLabel={warehouseShopifyName || undefined}
                shopLabel={shopShopifyName || undefined}
              />
            </div>

            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by product name, SKU, reference, color, bin location..."
                  value={inventorySearchTerm}
                  onChange={(e) => setInventorySearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            {isSaving && saveProgress.phase !== 'idle' && (
              <div className="mb-4 rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                    {saveProgress.phase === 'saving'
                      ? `Saving ${saveProgress.current} / ${saveProgress.total}…`
                      : `Pushing ${saveProgress.current} / ${saveProgress.total} to ${shopShopifyName || warehouseShopifyName || 'Shopify'}…`}
                  </span>
                  <Loader2 className="h-4 w-4 animate-spin text-blue-600 dark:text-blue-300" />
                </div>
                <div className="h-2 w-full rounded-full bg-blue-100 dark:bg-blue-950 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-blue-600 transition-all duration-300"
                    style={{ width: `${saveProgress.total > 0 ? Math.round((saveProgress.current / saveProgress.total) * 100) : 0}%` }}
                  />
                </div>
              </div>
            )}

            {!isSaving && saveResult && (
              <div
                className={`mb-4 rounded-xl border p-4 ${
                  saveResult.status === 'success'
                    ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20'
                    : saveResult.status === 'warning'
                      ? 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20'
                      : 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-2">
                    {saveResult.status === 'success' ? (
                      <Check className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
                    ) : saveResult.status === 'warning' ? (
                      <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
                    )}
                    <div>
                      <p className={`text-sm font-semibold ${
                        saveResult.status === 'success'
                          ? 'text-green-800 dark:text-green-200'
                          : saveResult.status === 'warning'
                            ? 'text-amber-800 dark:text-amber-200'
                            : 'text-red-800 dark:text-red-200'
                      }`}>
                        {saveResult.message}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        Saved locally: {saveResult.savedCount}
                        {saveResult.pushedToShopify && (
                          <> | Pushed+verified: {saveResult.pushedCount}</>
                        )}
                        {saveResult.skippedCount > 0 && (
                          <> | Skipped (no Shopify SKU): {saveResult.skippedCount}</>
                        )}
                        {saveResult.mismatchCount > 0 && (
                          <> | Mismatch: {saveResult.mismatchCount}</>
                        )}
                        {saveResult.failedCount > 0 && (
                          <> | Failed: {saveResult.failedCount}</>
                        )}
                      </p>
                      {saveResult.failedItems.length > 0 && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-sm text-gray-600 dark:text-gray-300 hover:underline">
                            View {saveResult.failedItems.length} item(s) needing attention
                          </summary>
                          <ul className="mt-2 max-h-48 overflow-auto space-y-1 text-xs text-gray-700 dark:text-gray-300">
                            {saveResult.failedItems.slice(0, FAILED_ITEMS_DISPLAY_CAP).map((f) => (
                              <li key={f.productId} className="border-b border-gray-200/60 dark:border-gray-700/60 pb-1">
                                <span className="font-medium">{f.name}</span>
                                {f.sku ? <span className="text-gray-500"> ({f.sku})</span> : null}
                                <span className="text-gray-500"> — {f.reason}</span>
                              </li>
                            ))}
                            {saveResult.failedItems.length > FAILED_ITEMS_DISPLAY_CAP && (
                              <li className="text-gray-500">
                                …and {saveResult.failedItems.length - FAILED_ITEMS_DISPLAY_CAP} more
                              </li>
                            )}
                          </ul>
                        </details>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setSaveResult(null)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                    aria-label="Dismiss"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            {(reportItemsLoading || locationDataLoading) ? (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-4"></div>
                <p className="text-gray-500 dark:text-gray-400">Loading inventory data...</p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">This may take a moment for large inventories</p>
              </div>
            ) : locationViewMode === 'difference' && warehouseLocation && shopLocation ? (
              <LocationComparisonTable
                products={locationProducts}
                warehouseLocation={warehouseLocation}
                comparisonLocation={shopLocation}
                colors={colors}
                sizes={sizes}
                searchTerm={inventorySearchTerm}
                stats={comparisonStats}
              />
            ) : (
              <ProductMatrixTable
                items={filteredReportItems}
                colors={colors}
                sizes={sizes}
                searchTerm={inventorySearchTerm}
                locationViewMode={locationViewMode}
                locationName={
                  locationViewMode === 'shop'
                    ? (shopShopifyName || shopLocation?.name)
                    : locationViewMode === 'warehouse'
                    ? (warehouseShopifyName || warehouseLocation?.name)
                    : undefined
                }
                productLocationMap={productLocationMap}
                targetLocationId={targetLocationId}
                binLocations={binLocationMap}
                isEditMode={isEditMode}
                pendingChanges={pendingChanges}
                onQuantityChange={handleQuantityChange}
              />
            )}
          </div>
        </div>
      )}

      {/* Shipments Tab */}
      {activeTab === 'shipments' && (
        <div className="space-y-6">
          <div className="card p-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-6 flex items-center">
              <ArrowUpDown className="h-6 w-6 text-blue-600 mr-3" />
              {t('reports.shipmentHistory')}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <button
                onClick={() => setShipmentFilter('all')}
                className={`p-6 rounded-xl border-2 transition-all cursor-pointer text-left ${
                  shipmentFilter === 'all'
                    ? 'bg-gray-100 dark:bg-gray-800 border-gray-400 dark:border-gray-600 shadow-lg'
                    : 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800 hover:border-gray-400 dark:hover:border-gray-600 hover:shadow-lg'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">All Shipments</p>
                    <p className="text-4xl font-bold text-gray-900 dark:text-gray-100 mt-2">{detailedShipments.length}</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                      {shipmentFilter === 'all' ? '✓ Showing all' : 'Click to show all'}
                    </p>
                  </div>
                  <Package className="h-16 w-16 text-gray-600 dark:text-gray-400 opacity-30" />
                </div>
              </button>

              <button
                onClick={() => setShipmentFilter('incoming')}
                className={`p-6 rounded-xl border-2 transition-all cursor-pointer text-left ${
                  shipmentFilter === 'incoming'
                    ? 'bg-green-100 dark:bg-green-900/30 border-green-400 dark:border-green-600 shadow-lg'
                    : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 hover:border-green-400 dark:hover:border-green-600 hover:shadow-lg'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-600 dark:text-green-400">{t('reports.incomingShipments')}</p>
                    <p className="text-4xl font-bold text-green-900 dark:text-green-100 mt-2">{stats.incomingShipments}</p>
                    <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                      {shipmentFilter === 'incoming' ? '✓ Showing incoming' : 'Click to filter'}
                    </p>
                  </div>
                  <Package className="h-16 w-16 text-green-600 dark:text-green-400 opacity-30" />
                </div>
              </button>

              <button
                onClick={() => setShipmentFilter('outgoing')}
                className={`p-6 rounded-xl border-2 transition-all cursor-pointer text-left ${
                  shipmentFilter === 'outgoing'
                    ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-400 dark:border-blue-600 shadow-lg'
                    : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 hover:border-blue-400 dark:hover:border-blue-600 hover:shadow-lg'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-600 dark:text-blue-400">{t('reports.outgoingShipments')}</p>
                    <p className="text-4xl font-bold text-blue-900 dark:text-blue-100 mt-2">{stats.outgoingShipments}</p>
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                      {shipmentFilter === 'outgoing' ? '✓ Showing outgoing' : 'Click to filter'}
                    </p>
                  </div>
                  <Package className="h-16 w-16 text-blue-600 dark:text-blue-400 opacity-30" />
                </div>
              </button>
            </div>

            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search shipments by tracking number, supplier, or status..."
                  value={shipmentSearchTerm}
                  onChange={(e) => setShipmentSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-100 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">{t('reports.table.date')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">{t('reports.table.type')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Details</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Tracking</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">{t('reports.table.status')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loadingShipments ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                        Loading shipments...
                      </td>
                    </tr>
                  ) : detailedShipments
                      .filter(shipment => shipmentFilter === 'all' || shipment.type === shipmentFilter)
                      .filter(shipment => {
                        if (!shipmentSearchTerm) return true;
                        const searchLower = shipmentSearchTerm.toLowerCase();
                        return (
                          shipment.tracking_number?.toLowerCase().includes(searchLower) ||
                          shipment.supplier_name?.toLowerCase().includes(searchLower) ||
                          shipment.recipient_name?.toLowerCase().includes(searchLower) ||
                          shipment.status?.toLowerCase().includes(searchLower) ||
                          shipment.carrier?.toLowerCase().includes(searchLower)
                        );
                      }).length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                        {shipmentSearchTerm ? 'No matching shipments found' :
                          shipmentFilter === 'all'
                          ? t('reports.noShipments')
                          : `No ${shipmentFilter} shipments found`
                        }
                      </td>
                    </tr>
                  ) : (
                    detailedShipments
                      .filter(shipment => shipmentFilter === 'all' || shipment.type === shipmentFilter)
                      .filter(shipment => {
                        if (!shipmentSearchTerm) return true;
                        const searchLower = shipmentSearchTerm.toLowerCase();
                        return (
                          shipment.tracking_number?.toLowerCase().includes(searchLower) ||
                          shipment.supplier_name?.toLowerCase().includes(searchLower) ||
                          shipment.recipient_name?.toLowerCase().includes(searchLower) ||
                          shipment.status?.toLowerCase().includes(searchLower) ||
                          shipment.carrier?.toLowerCase().includes(searchLower)
                        );
                      })
                      .map((shipment) => (
                      <tr key={`${shipment.type}-${shipment.id}`} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                          {new Date(shipment.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            shipment.type === 'incoming'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                              : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                          }`}>
                            {shipment.type === 'incoming' ? 'Incoming' : 'Outgoing'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                          <div>
                            <p className="font-medium">{shipment.customer_name || shipment.supplier_name}</p>
                            {shipment.shipment_number && (
                              <p className="text-xs text-gray-500 dark:text-gray-400">{shipment.shipment_number}</p>
                            )}
                            {shipment.shipment_items && shipment.shipment_items.length > 0 && (
                              <div className="mt-2 text-xs">
                                <p className="font-medium text-gray-700 dark:text-gray-300">{shipment.shipment_items.length} item(s):</p>
                                <div className="mt-1.5 space-y-1.5">
                                  {shipment.shipment_items.slice(0, 3).map((item: any) => {
                                    const colorName = getColorName(item.product?.color_id);
                                    const sizeName = getSizeName(item.product?.size_id);
                                    const qty = shipment.type === 'incoming'
                                      ? (item.received_quantity || item.expected_quantity)
                                      : item.quantity;
                                    return (
                                      <div key={item.id} className="flex flex-wrap items-center gap-1.5 text-gray-600 dark:text-gray-400">
                                        <span className="font-medium">{item.product?.name || 'Unknown'}</span>
                                        <span className="text-gray-400 dark:text-gray-500">-</span>
                                        <span>{qty} units</span>
                                        {(colorName || sizeName) && (
                                          <div className="flex items-center gap-1">
                                            {colorName && (
                                              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-[10px] font-medium text-gray-600 dark:text-gray-300">
                                                {colorName}
                                              </span>
                                            )}
                                            {sizeName && (
                                              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-[10px] font-medium text-blue-600 dark:text-blue-400">
                                                {sizeName}
                                              </span>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                  {shipment.shipment_items.length > 3 && (
                                    <p className="text-gray-500 dark:text-gray-500 italic">
                                      +{shipment.shipment_items.length - 3} more...
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {editingShipment === shipment.id && shipment.type === 'outgoing' ? (
                            <div className="space-y-2">
                              <input
                                type="text"
                                value={(editForm as any).tracking_code || ''}
                                onChange={(e) => setEditForm({ ...editForm, tracking_code: e.target.value } as any)}
                                placeholder="Add tracking code"
                                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                              />
                              {(editForm as any).tracking_code && (() => {
                                const carrierInfo = detectCarrier((editForm as any).tracking_code);
                                return (
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getCarrierColor(carrierInfo.carrier)}`}>
                                    {carrierInfo.name}
                                  </span>
                                );
                              })()}
                            </div>
                          ) : shipment.type === 'outgoing' && shipment.tracking_code ? (
                            <div className="space-y-1">
                              <p className="font-mono text-xs">{shipment.tracking_code}</p>
                              {shipment.carrier && (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getCarrierColor(shipment.carrier)}`}>
                                  {shipment.carrier}
                                </span>
                              )}
                            </div>
                          ) : shipment.type === 'outgoing' ? (
                            <span className="text-xs text-gray-400">No tracking</span>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {editingShipment === shipment.id ? (
                            <select
                              value={(editForm as any).status || shipment.status}
                              onChange={(e) => setEditForm({ ...editForm, status: e.target.value } as any)}
                              className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                            >
                              <option value="pending">Pending</option>
                              <option value="shipped">Shipped</option>
                              <option value="received">Received</option>
                              <option value="completed">Completed</option>
                            </select>
                          ) : (
                            <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                              {shipment.status || 'Completed'}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {editingShipment === shipment.id ? (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleSaveEdit(shipment)}
                                className="text-green-600 hover:text-green-700 dark:text-green-400"
                                title="Save"
                              >
                                <Save className="h-4 w-4" />
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="text-gray-600 hover:text-gray-700 dark:text-gray-400"
                                title="Cancel"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handlePrintShipment(shipment)}
                                className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 hover:text-gray-800 hover:bg-gray-200 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-600 transition-colors"
                                title="Print Details"
                              >
                                <Printer className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleEditShipment(shipment)}
                                className="p-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-600 hover:text-blue-700 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-blue-900/40 transition-colors"
                                title="Edit"
                              >
                                <Edit2 className="h-4 w-4" />
                              </button>
                              {shipment.type === 'outgoing' && shipment.tracking_code && (
                                <a
                                  href={`/package-tracing`}
                                  className="p-1.5 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-600 hover:text-green-700 hover:bg-green-100 dark:text-green-400 dark:hover:bg-green-900/40 transition-colors"
                                  title="View Tracking"
                                >
                                  <MapPin className="h-4 w-4" />
                                </a>
                              )}
                              <button
                                onClick={() => handleDeleteShipment(shipment)}
                                className="p-1.5 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 hover:text-red-700 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/40 transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Activity Log Tab */}
      {activeTab === 'activity' && (
        <div className="card p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 flex items-center">
              <Clock className="h-6 w-6 text-blue-600 mr-3" />
              {t('reports.activityLog')}
            </h2>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {activityLogs.length} activities
              </span>
            </div>
          </div>

          {(() => {
            const getActivityIcon = (actionType: string) => {
              switch (actionType) {
                case 'auth':
                  return activityLogs.find((l: any) => l.action_type === actionType && l.description?.includes('logged out'))
                    ? LogOut : LogIn;
                case 'invoice':
                  return Receipt;
                case 'invoice_cancelled':
                  return FileX;
                case 'incoming_shipment':
                  return PackagePlus;
                case 'outgoing_shipment':
                  return PackageMinus;
                default:
                  return Activity;
              }
            };

            const getActivityColor = (actionType: string, description?: string) => {
              if (actionType === 'auth' && description?.includes('logged out')) {
                return { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-600 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-800' };
              }
              switch (actionType) {
                case 'auth':
                  return { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-600 dark:text-green-400', border: 'border-green-200 dark:border-green-800' };
                case 'invoice':
                  return { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-600 dark:text-blue-400', border: 'border-blue-200 dark:border-blue-800' };
                case 'invoice_cancelled':
                  return { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-600 dark:text-red-400', border: 'border-red-200 dark:border-red-800' };
                case 'incoming_shipment':
                  return { bg: 'bg-teal-100 dark:bg-teal-900/30', text: 'text-teal-600 dark:text-teal-400', border: 'border-teal-200 dark:border-teal-800' };
                case 'outgoing_shipment':
                  return { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-600 dark:text-orange-400', border: 'border-orange-200 dark:border-orange-800' };
                default:
                  return { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-600 dark:text-gray-400', border: 'border-gray-200 dark:border-gray-700' };
              }
            };

            const getActivityLabel = (actionType: string, description?: string) => {
              if (actionType === 'auth' && description?.includes('logged out')) return 'Logout';
              if (actionType === 'auth') return 'Login';
              if (actionType === 'invoice') return 'Invoice Created';
              if (actionType === 'invoice_cancelled') return 'Invoice Cancelled';
              if (actionType === 'incoming_shipment') return 'Incoming Shipment';
              if (actionType === 'outgoing_shipment') return 'Outgoing Shipment';
              return actionType;
            };

            const formatTime = (dateStr: string) => {
              const date = new Date(dateStr);
              return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
            };

            const formatDate = (dateStr: string) => {
              const date = new Date(dateStr);
              const today = new Date();
              const yesterday = new Date(today);
              yesterday.setDate(yesterday.getDate() - 1);

              if (date.toDateString() === today.toDateString()) return 'Today';
              if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
              return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
            };

            const groupedLogs = activityLogs.reduce((groups: any, log: any) => {
              const date = new Date(log.created_at).toDateString();
              if (!groups[date]) groups[date] = [];
              groups[date].push(log);
              return groups;
            }, {});

            return (
              <div className="space-y-6">
                {activityLogs.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                      <Activity className="h-10 w-10 text-gray-400 dark:text-gray-500" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No Activity Found</h3>
                    <p className="text-gray-500 dark:text-gray-400">There are no activities recorded for the selected date range.</p>
                  </div>
                ) : (
                  Object.entries(groupedLogs).map(([dateKey, logs]: [string, any]) => (
                    <div key={dateKey}>
                      <div className="sticky top-0 bg-white dark:bg-gray-900 py-2 z-10">
                        <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          {formatDate(logs[0].created_at)}
                          <span className="text-xs font-normal text-gray-400 dark:text-gray-500">({logs.length} activities)</span>
                        </h3>
                      </div>
                      <div className="space-y-3 mt-3">
                        {logs.map((log: any, index: number) => {
                          const colors = getActivityColor(log.action_type, log.description);
                          const IconComponent = log.action_type === 'auth' && log.description?.includes('logged out') ? LogOut :
                            log.action_type === 'auth' ? LogIn :
                            log.action_type === 'invoice' ? Receipt :
                            log.action_type === 'invoice_cancelled' ? FileX :
                            log.action_type === 'incoming_shipment' ? PackagePlus :
                            log.action_type === 'outgoing_shipment' ? PackageMinus : Activity;

                          return (
                            <div
                              key={log.id || index}
                              className={`relative flex items-start gap-4 p-4 bg-white dark:bg-gray-800 rounded-xl border ${colors.border} shadow-sm hover:shadow-md transition-all duration-200`}
                            >
                              <div className={`flex-shrink-0 w-12 h-12 rounded-xl ${colors.bg} flex items-center justify-center`}>
                                <IconComponent className={`h-6 w-6 ${colors.text}`} />
                              </div>

                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}>
                                        {getActivityLabel(log.action_type, log.description)}
                                      </span>
                                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                        {log.user?.full_name || log.user?.email || 'Unknown User'}
                                      </span>
                                    </div>

                                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                                      {log.description}
                                    </p>

                                    {log.metadata && (
                                      <div className="mt-2 flex flex-wrap gap-2">
                                        {log.metadata.invoice_number && (
                                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-xs text-gray-600 dark:text-gray-300">
                                            <Receipt className="h-3 w-3" />
                                            {log.metadata.invoice_number}
                                          </span>
                                        )}
                                        {log.metadata.shipment_number && (
                                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-xs text-gray-600 dark:text-gray-300">
                                            <Package className="h-3 w-3" />
                                            {log.metadata.shipment_number}
                                          </span>
                                        )}
                                        {log.metadata.customer_name && (
                                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-xs text-gray-600 dark:text-gray-300">
                                            <User className="h-3 w-3" />
                                            {log.metadata.customer_name}
                                          </span>
                                        )}
                                        {log.metadata.supplier_name && (
                                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-xs text-gray-600 dark:text-gray-300">
                                            <User className="h-3 w-3" />
                                            {log.metadata.supplier_name}
                                          </span>
                                        )}
                                        {log.metadata.total_amount !== undefined && (
                                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-xs text-gray-600 dark:text-gray-300">
                                            <DollarSign className="h-3 w-3" />
                                            {typeof log.metadata.total_amount === 'number' ? `€${log.metadata.total_amount.toFixed(2)}` : log.metadata.total_amount}
                                          </span>
                                        )}
                                        {log.metadata.items_count && (
                                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-xs text-gray-600 dark:text-gray-300">
                                            <Package className="h-3 w-3" />
                                            {log.metadata.items_count} items
                                          </span>
                                        )}
                                        {log.metadata.total_quantity && (
                                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-xs text-gray-600 dark:text-gray-300">
                                            +{log.metadata.total_quantity} units
                                          </span>
                                        )}
                                        {log.metadata.cancellation_reason && (
                                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-red-100 dark:bg-red-900/30 text-xs text-red-600 dark:text-red-400">
                                            <AlertTriangle className="h-3 w-3" />
                                            {log.metadata.cancellation_reason}
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>

                                  <div className="flex-shrink-0 text-right">
                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                      {formatTime(log.created_at)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* Worker Fulfillment Tab */}
      {activeTab === 'worker_fulfillment' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400 mr-2">
              {t('reports.workerFulfillment.title')}:
            </span>
            <div className="flex rounded-lg border border-gray-200 dark:border-gray-600 p-0.5 bg-gray-100 dark:bg-gray-800">
              <button
                type="button"
                onClick={() => setWorkerFulfillmentPreset('1h')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  workerFulfillmentRange === '1h'
                    ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                {t('reports.workerFulfillment.last1Hour')}
              </button>
              <button
                type="button"
                onClick={() => setWorkerFulfillmentPreset('1d')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  workerFulfillmentRange === '1d'
                    ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                {t('reports.workerFulfillment.last1Day')}
              </button>
              <button
                type="button"
                onClick={() => setWorkerFulfillmentPreset('7d')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  workerFulfillmentRange === '7d'
                    ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                {t('reports.workerFulfillment.last7Days')}
              </button>
            </div>
          </div>
          <WorkerFulfillmentSection
            stats={workerFulfillmentStats.stats}
            loading={workerFulfillmentStats.loading}
            error={workerFulfillmentStats.error}
            title={t('reports.workerFulfillment.title')}
            subtitle={t('reports.workerFulfillment.subtitle')}
            readyLabel={t('reports.workerFulfillment.ready')}
            fulfilledLabel={t('reports.workerFulfillment.fulfilled')}
            targetLabel={t('reports.workerFulfillment.target')}
          />
        </div>
      )}

      {/* Invoices Tab */}
      {activeTab === 'invoices' && (
        <div className="space-y-6">
          <div className="card p-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-6 flex items-center">
              <Receipt className="h-6 w-6 text-blue-600 mr-3" />
              {t('reports.salesInvoices')}
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
              <div className="p-5 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl border border-blue-200 dark:border-blue-700">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase">{t('reports.totalInvoices')}</p>
                </div>
                <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{invoiceStats.totalInvoices}</p>
              </div>
              <div className="p-5 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-xl border border-green-200 dark:border-green-700">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="h-4 w-4 text-green-600 dark:text-green-400" />
                  <p className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase">{t('reports.totalRevenue')}</p>
                </div>
                <p className="text-2xl font-bold text-green-900 dark:text-green-100">{invoiceStats.totalRevenue.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</p>
              </div>
              <div className="p-5 bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 rounded-xl border border-amber-200 dark:border-amber-700">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase">{t('reports.pendingPayments')}</p>
                </div>
                <p className="text-2xl font-bold text-amber-900 dark:text-amber-100">{invoiceStats.pendingPayments}</p>
              </div>
              <div className="p-5 bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 rounded-xl border border-red-200 dark:border-red-700">
                <div className="flex items-center gap-2 mb-2">
                  <Ban className="h-4 w-4 text-red-600 dark:text-red-400" />
                  <p className="text-xs font-semibold text-red-600 dark:text-red-400 uppercase">Cancelled</p>
                </div>
                <p className="text-2xl font-bold text-red-900 dark:text-red-100">{invoiceStats.cancelledInvoices}</p>
              </div>
              <div className="p-5 bg-gradient-to-br from-cyan-50 to-cyan-100 dark:from-cyan-900/20 dark:to-cyan-800/20 rounded-xl border border-cyan-200 dark:border-cyan-700">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />
                  <p className="text-xs font-semibold text-cyan-600 dark:text-cyan-400 uppercase">{t('reports.avgInvoiceValue')}</p>
                </div>
                <p className="text-2xl font-bold text-cyan-900 dark:text-cyan-100">{invoiceStats.avgInvoiceValue.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</p>
              </div>
            </div>

            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search invoices by number, customer, or amount..."
                  value={invoiceSearchTerm}
                  onChange={(e) => setInvoiceSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-750">
                  <tr>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">{t('reports.table.date')}</th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">{t('reports.table.invoiceNumber')}</th>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">{t('reports.table.customer')}</th>
                    <th className="px-4 py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">{t('reports.table.items')}</th>
                    <th className="px-4 py-4 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">{t('reports.table.amount')}</th>
                    <th className="px-4 py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">{t('reports.table.status')}</th>
                    <th className="px-4 py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {invoices.filter((invoice) => {
                    if (!invoiceSearchTerm) return true;
                    const searchLower = invoiceSearchTerm.toLowerCase();
                    return (
                      invoice.invoice_number?.toLowerCase().includes(searchLower) ||
                      invoice.customer_name?.toLowerCase().includes(searchLower) ||
                      invoice.total_amount?.toString().includes(searchLower) ||
                      invoice.payment_status?.toLowerCase().includes(searchLower)
                    );
                  }).length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-16 text-center text-gray-500 dark:text-gray-400">
                        <Receipt className="h-16 w-16 mx-auto mb-4 opacity-30" />
                        <p className="text-lg font-medium">{invoiceSearchTerm ? 'No matching invoices found' : t('reports.noInvoices')}</p>
                        <p className="text-sm mt-2 text-gray-400">{invoiceSearchTerm ? 'Try a different search term' : t('reports.invoicesFromTerminal')}</p>
                      </td>
                    </tr>
                  ) : (
                    invoices.filter((invoice) => {
                      if (!invoiceSearchTerm) return true;
                      const searchLower = invoiceSearchTerm.toLowerCase();
                      return (
                        invoice.invoice_number?.toLowerCase().includes(searchLower) ||
                        invoice.customer_name?.toLowerCase().includes(searchLower) ||
                        invoice.total_amount?.toString().includes(searchLower) ||
                        invoice.payment_status?.toLowerCase().includes(searchLower)
                      );
                    }).map((invoice, index) => (
                      <tr
                        key={invoice.id}
                        className={`hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors ${
                          invoice.payment_status === 'cancelled' ? 'bg-red-50/30 dark:bg-red-900/10' :
                          index % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50/50 dark:bg-gray-800/50'
                        }`}
                      >
                        <td className="px-4 py-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {new Date(invoice.invoice_date).toLocaleDateString()}
                          </div>
                          {invoice.cancelled_at && (
                            <div className="text-xs text-red-500 dark:text-red-400 mt-1 flex items-center gap-1">
                              <XCircle className="h-3 w-3" />
                              Cancelled: {new Date(invoice.cancelled_at).toLocaleDateString()}
                            </div>
                          )}
                          {invoice.paid_at && invoice.payment_status === 'paid' && (
                            <div className="text-xs text-green-500 dark:text-green-400 mt-1 flex items-center gap-1">
                              <Check className="h-3 w-3" />
                              Paid: {new Date(invoice.paid_at).toLocaleDateString()}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <span className={`text-sm font-semibold ${invoice.payment_status === 'cancelled' ? 'text-gray-400 line-through' : 'text-gray-900 dark:text-gray-100'}`}>
                            {invoice.invoice_number}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {invoice.customer_name || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300">
                            {invoice.invoice_items?.length || 0}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-right">
                          <span className={`text-sm font-bold tabular-nums ${invoice.payment_status === 'cancelled' ? 'text-gray-400 line-through' : 'text-gray-900 dark:text-gray-100'}`}>
                            {parseFloat(invoice.total_amount || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <div className="flex flex-col items-center gap-2">
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${
                              invoice.payment_status === 'paid'
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                                : invoice.payment_status === 'cancelled'
                                ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                                : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                            }`}>
                              {invoice.payment_status === 'paid' && <Check className="h-3.5 w-3.5" />}
                              {invoice.payment_status === 'cancelled' && <Ban className="h-3.5 w-3.5" />}
                              {invoice.payment_status === 'unpaid' && <Clock className="h-3.5 w-3.5" />}
                              {invoice.payment_status === 'paid' ? 'Paid' :
                               invoice.payment_status === 'cancelled' ? 'Cancelled' : 'Unpaid'}
                            </span>
                            {invoice.payment_status === 'cancelled' && invoice.cancellation_reason && (
                              <p className="text-xs text-red-500 dark:text-red-400 mt-1 max-w-32 text-center line-clamp-2" title={invoice.cancellation_reason}>
                                {invoice.cancellation_reason}
                              </p>
                            )}
                            {invoice.payment_status === 'unpaid' && (
                              <button
                                onClick={() => handleMarkAsPaid(invoice)}
                                disabled={processingInvoice === invoice.id}
                                className="text-xs text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 font-medium flex items-center gap-1 hover:underline disabled:opacity-50"
                              >
                                {processingInvoice === invoice.id ? (
                                  <span className="animate-pulse">Processing...</span>
                                ) : (
                                  <>
                                    <Check className="h-3 w-3" />
                                    Mark as Paid
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => window.open(`/invoice/${invoice.id}`, '_blank')}
                              className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-all hover:shadow-md"
                            >
                              <Printer className="h-3.5 w-3.5" />
                              Print
                            </button>
                            {invoice.payment_status !== 'cancelled' && (
                              <button
                                onClick={() => setShowCancelModal(invoice.id)}
                                disabled={processingInvoice === invoice.id}
                                className="inline-flex items-center gap-1.5 px-3 py-2 bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 text-xs font-medium rounded-lg transition-all disabled:opacity-50"
                              >
                                <XCircle className="h-3.5 w-3.5" />
                                Cancel
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {invoices.length > 0 && (
              <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                Showing {invoices.filter((invoice) => {
                  if (!invoiceSearchTerm) return true;
                  const searchLower = invoiceSearchTerm.toLowerCase();
                  return (
                    invoice.invoice_number?.toLowerCase().includes(searchLower) ||
                    invoice.customer_name?.toLowerCase().includes(searchLower) ||
                    invoice.total_amount?.toString().includes(searchLower) ||
                    invoice.payment_status?.toLowerCase().includes(searchLower)
                  );
                }).length} of {invoices.length} invoices
              </div>
            )}
          </div>

          {showCancelModal && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
                <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <AlertTriangle className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">Cancel Invoice</h3>
                      <p className="text-red-100 text-sm">This action cannot be undone</p>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <div className="mb-6">
                    <p className="text-gray-700 dark:text-gray-300 mb-4">
                      Are you sure you want to cancel this invoice? This will:
                    </p>
                    <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                      <li className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-red-500"></div>
                        Restore all items back to inventory
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-red-500"></div>
                        Remove the invoice from revenue calculations
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-red-500"></div>
                        Record the cancellation date and reason
                      </li>
                    </ul>
                  </div>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Cancellation Reason (optional)
                    </label>
                    <textarea
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      placeholder="Enter reason for cancellation..."
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => { setShowCancelModal(null); setCancelReason(''); }}
                      className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-xl transition-colors"
                    >
                      Keep Invoice
                    </button>
                    <button
                      onClick={() => {
                        const invoice = invoices.find(inv => inv.id === showCancelModal);
                        if (invoice) handleCancelInvoice(invoice);
                      }}
                      disabled={processingInvoice === showCancelModal}
                      className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {processingInvoice === showCancelModal ? (
                        <span className="animate-pulse">Processing...</span>
                      ) : (
                        <>
                          <XCircle className="h-4 w-4" />
                          Cancel Invoice
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Missing Order Analysis Tab */}
      {activeTab === 'missing_order_analysis' && (
        <MissingOrderAnalysisReport adminId={adminId} user={user} />
      )}

      {activeTab === 'reservations' && (
        <ReservationsReport />
      )}

      {activeTab === 'shop_receipts' && (
        <ShopReceiptsReport />
      )}

      {activeTab === 'shop_stock_qty' && (
        <ShopStockQtyReport />
      )}

      {activeTab === 'returns' && (
        <ReturnsReport />
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Target className="h-7 w-7 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  Enterprise Analytics Dashboard
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Comprehensive business intelligence and insights
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <TimeRangeSelector
                selectedRange={analyticsTimeRange}
                onRangeChange={setAnalyticsTimeRange}
                onCustomRangeChange={handleAnalyticsCustomRangeChange}
                customStartDate={analyticsTimeRange.customStart}
                customEndDate={analyticsTimeRange.customEnd}
                showComparison={false}
              />

              <Button
                variant="secondary"
                onClick={handleRefreshAnalytics}
                disabled={refreshingAnalytics || enterpriseAnalytics.loading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshingAnalytics || enterpriseAnalytics.loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>

              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  onClick={async () => {
                    await exportAnalyticsToExcel(overviewAnalytics, enterpriseAnalytics, analyticsTimeRange);
                  }}
                  disabled={enterpriseAnalytics.loading}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export Excel
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    exportAnalyticsToPDF(overviewAnalytics, enterpriseAnalytics, analyticsTimeRange);
                  }}
                  disabled={enterpriseAnalytics.loading}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export PDF
                </Button>
              </div>
            </div>
          </div>

          {/* Last Updated */}
          {(overviewAnalytics.lastUpdated || enterpriseAnalytics.lastUpdated) && (
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <Clock className="h-3 w-3" />
              Last updated: {(overviewAnalytics.lastUpdated || enterpriseAnalytics.lastUpdated)?.toLocaleTimeString()}
            </div>
          )}

          {/* Error Display */}
          {(overviewAnalytics.error || enterpriseAnalytics.error) && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
              <p className="text-sm text-red-600 dark:text-red-400">
                {overviewAnalytics.error || enterpriseAnalytics.error}
              </p>
            </div>
          )}

          {/* Analytics Sub-Tabs */}
          <div className="flex items-center gap-2 border-b border-gray-200 dark:border-gray-700">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'profit', label: 'Profit Analysis' },
              { id: 'products', label: 'Products' },
              { id: 'forecasting', label: 'Forecasting' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setAnalyticsActiveTab(tab.id as any)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  analyticsActiveTab === tab.id
                    ? 'border-cyan-500 text-cyan-600 dark:text-cyan-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Analytics Sub-Tab Content */}
          {analyticsActiveTab === 'overview' && (
            <div className="space-y-6">
              <div className="flex justify-end gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={async () => { await exportAnalyticsOverviewToExcel(overviewAnalytics, analyticsTimeRange); }}
                  disabled={enterpriseAnalytics.loading || overviewAnalytics.loading}
                >
                  <Download className="h-4 w-4 mr-1" />
                  Export Excel
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => exportAnalyticsOverviewToPDF(overviewAnalytics, analyticsTimeRange)}
                  disabled={enterpriseAnalytics.loading || overviewAnalytics.loading}
                >
                  <Download className="h-4 w-4 mr-1" />
                  Export PDF
                </Button>
              </div>
              {/* Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <OverviewMetricCard
                  title="Total Inventory Value"
                  value={overviewAnalytics.inventory.totalValue}
                  subtitle={`${overviewAnalytics.inventory.totalItems.toLocaleString()} unique items`}
                  icon={Package}
                  trend={{
                    value: overviewAnalytics.inventory.trends.value.changePercent,
                    positive: overviewAnalytics.inventory.trends.value.isPositive,
                  }}
                  gradient="from-emerald-500 to-green-600"
                  format="currency"
                  loading={enterpriseAnalytics.loading || overviewAnalytics.loading}
                />

                <OverviewMetricCard
                  title="Period Revenue"
                  value={overviewAnalytics.sales.totalRevenue}
                  subtitle={`${overviewAnalytics.sales.totalOrders} orders`}
                  icon={TrendingUp}
                  trend={{
                    value: overviewAnalytics.sales.trends.revenue.changePercent,
                    positive: overviewAnalytics.sales.trends.revenue.isPositive,
                  }}
                  gradient="from-blue-500 to-cyan-600"
                  format="currency"
                  loading={enterpriseAnalytics.loading || overviewAnalytics.loading}
                />

                <OverviewMetricCard
                  title="Stock Items"
                  value={overviewAnalytics.inventory.totalQuantity}
                  subtitle={`${overviewAnalytics.inventory.categories} categories`}
                  icon={Package}
                  trend={{
                    value: overviewAnalytics.inventory.trends.quantity.changePercent,
                    positive: overviewAnalytics.inventory.trends.quantity.isPositive,
                  }}
                  gradient="from-teal-500 to-cyan-600"
                  format="count"
                  loading={enterpriseAnalytics.loading || overviewAnalytics.loading}
                />

                <OverviewMetricCard
                  title="Fulfillment Rate"
                  value={overviewAnalytics.performance.fulfillmentRate}
                  subtitle={`${overviewAnalytics.shipments.delivered} delivered`}
                  icon={Truck}
                  gradient="from-amber-500 to-orange-600"
                  format="percentage"
                  loading={enterpriseAnalytics.loading || overviewAnalytics.loading}
                />
              </div>

              {/* Charts and Performance */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                  {/* Revenue Trend */}
                  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center">
                          <TrendingUp className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            Revenue Trend
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Sales performance over time
                          </p>
                        </div>
                      </div>
                    </div>
                    {overviewAnalytics.sales.revenueTrend.length > 0 ? (
                      <OverviewLineChart
                        data={overviewAnalytics.sales.revenueTrend}
                        title=""
                        color="blue"
                        height={250}
                      />
                    ) : (
                      <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
                        No revenue data available
                      </div>
                    )}
                  </div>

                  {/* Stock Alerts */}
                  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center">
                        <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          Stock Alerts
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Items requiring attention
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-amber-800 dark:text-amber-300">Low Stock</span>
                          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                        </div>
                        <p className="text-3xl font-bold text-amber-700 dark:text-amber-300">
                          {overviewAnalytics.inventory.lowStockCount}
                        </p>
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">items below threshold</p>
                      </div>

                      <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-red-800 dark:text-red-300">Out of Stock</span>
                          <Package className="h-4 w-4 text-red-600 dark:text-red-400" />
                        </div>
                        <p className="text-3xl font-bold text-red-700 dark:text-red-300">
                          {overviewAnalytics.inventory.outOfStockCount}
                        </p>
                        <p className="text-xs text-red-600 dark:text-red-400 mt-1">items need reorder</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Performance KPIs */}
                  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                        <Activity className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        Performance KPIs
                      </h3>
                    </div>

                    <PerformanceMetrics
                      metrics={[
                        {
                          label: 'Inventory Turnover',
                          value: overviewAnalytics.performance.inventoryTurnover,
                          unit: 'x',
                          color: 'text-violet-500',
                        },
                        {
                          label: 'Fulfillment Rate',
                          value: overviewAnalytics.performance.fulfillmentRate,
                          unit: '%',
                          color: 'text-blue-500',
                        },
                      ]}
                      title=""
                    />
                  </div>

                  {/* Quick Stats */}
                  <div className="bg-gradient-to-br from-cyan-500 to-teal-600 rounded-2xl p-6 text-white">
                    <div className="flex items-center gap-3 mb-4">
                      <Zap className="h-6 w-6" />
                      <h3 className="text-lg font-semibold">Quick Stats</h3>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-cyan-100">Categories</span>
                        <span className="font-bold">{overviewAnalytics.inventory.categories}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-cyan-100">Avg Item Value</span>
                        <span className="font-bold">
                          €{overviewAnalytics.inventory.avgItemValue.toLocaleString('nl-NL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-cyan-100">Total Orders</span>
                        <span className="font-bold">{overviewAnalytics.sales.totalOrders}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {analyticsActiveTab === 'profit' && (
            <div className="space-y-6">
              <div className="flex justify-end gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={async () => { await exportAnalyticsProfitToExcel(enterpriseAnalytics.profit, analyticsTimeRange); }}
                  disabled={enterpriseAnalytics.loading}
                >
                  <Download className="h-4 w-4 mr-1" />
                  Export Excel
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => exportAnalyticsProfitToPDF(enterpriseAnalytics.profit, analyticsTimeRange)}
                  disabled={enterpriseAnalytics.loading}
                >
                  <Download className="h-4 w-4 mr-1" />
                  Export PDF
                </Button>
              </div>
              <ProfitAnalysis
                profit={enterpriseAnalytics.profit}
                revenueTrend={overviewAnalytics.sales.revenueTrend}
                loading={enterpriseAnalytics.loading || overviewAnalytics.loading}
              />
            </div>
          )}

          {analyticsActiveTab === 'products' && (
            <div className="space-y-6">
              <div className="flex justify-end gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={async () => { await exportAnalyticsProductsToExcel(enterpriseAnalytics.products, analyticsTimeRange); }}
                  disabled={enterpriseAnalytics.loading}
                >
                  <Download className="h-4 w-4 mr-1" />
                  Export Excel
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => exportAnalyticsProductsToPDF(enterpriseAnalytics.products, analyticsTimeRange)}
                  disabled={enterpriseAnalytics.loading}
                >
                  <Download className="h-4 w-4 mr-1" />
                  Export PDF
                </Button>
              </div>
              <ProductPerformance
                topProducts={enterpriseAnalytics.products.topProducts}
                bottomProducts={enterpriseAnalytics.products.bottomProducts}
                abcAnalysis={enterpriseAnalytics.products.abcAnalysis}
                loading={enterpriseAnalytics.loading}
              />
            </div>
          )}

          {analyticsActiveTab === 'forecasting' && (
            <div className="space-y-6">
              <div className="flex justify-end gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={async () => { await exportAnalyticsForecastingToExcel(enterpriseAnalytics.forecasting, analyticsTimeRange, overviewAnalytics.sales.revenueTrend); }}
                  disabled={enterpriseAnalytics.loading}
                >
                  <Download className="h-4 w-4 mr-1" />
                  Export Excel
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => exportAnalyticsForecastingToPDF(enterpriseAnalytics.forecasting, analyticsTimeRange, overviewAnalytics.sales.revenueTrend)}
                  disabled={enterpriseAnalytics.loading}
                >
                  <Download className="h-4 w-4 mr-1" />
                  Export PDF
                </Button>
              </div>
              <ForecastingChart
                forecast={enterpriseAnalytics.forecasting.revenueForecast}
                title="Revenue Forecast"
                historicalData={overviewAnalytics.sales.revenueTrend}
                loading={enterpriseAnalytics.loading || overviewAnalytics.loading}
              />
            </div>
          )}

          {/* Loading State */}
          {(enterpriseAnalytics.loading || overviewAnalytics.loading) && !overviewAnalytics.lastUpdated && (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <Loader2 className="h-12 w-12 animate-spin text-cyan-600 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">Loading analytics...</p>
              </div>
            </div>
          )}
        </div>
      )}

      {showPrintModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className={`px-6 py-4 ${showPrintModal.type === 'incoming' ? 'bg-gradient-to-r from-green-500 to-emerald-600' : 'bg-gradient-to-r from-blue-500 to-blue-600'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <Package className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Shipment Details</h3>
                    <p className="text-white/80 text-sm">{showPrintModal.shipment_number}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPrintModal(null)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors text-white"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Type</p>
                  <p className="font-semibold text-gray-900 dark:text-gray-100 capitalize">{showPrintModal.type}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                    {showPrintModal.type === 'incoming' ? 'Supplier' : 'Customer'}
                  </p>
                  <p className="font-semibold text-gray-900 dark:text-gray-100">
                    {showPrintModal.customer_name || showPrintModal.supplier_name || '-'}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Date</p>
                  <p className="font-semibold text-gray-900 dark:text-gray-100">
                    {new Date(showPrintModal.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Status</p>
                  <p className="font-semibold text-gray-900 dark:text-gray-100">{showPrintModal.status || 'Completed'}</p>
                </div>
              </div>

              {showPrintModal.type === 'outgoing' && showPrintModal.tracking_code && (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 mb-6">
                  <p className="text-xs text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-1">Tracking Code</p>
                  <div className="flex items-center gap-2">
                    <p className="font-mono font-semibold text-blue-800 dark:text-blue-200">{showPrintModal.tracking_code}</p>
                    {showPrintModal.carrier && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getCarrierColor(showPrintModal.carrier)}`}>
                        {showPrintModal.carrier}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {showPrintModal.type === 'outgoing' && showPrintModal.shipping_address && (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 mb-6">
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Shipping Address</p>
                  <p className="font-semibold text-gray-900 dark:text-gray-100">{showPrintModal.shipping_address}</p>
                </div>
              )}

              <div className="border dark:border-gray-700 rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-700/50">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">Product</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">SKU</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">Color</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">Size</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">Qty</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {showPrintModal.shipment_items?.map((item: any) => {
                      const colorName = getColorName(item.product?.color_id);
                      const sizeName = getSizeName(item.product?.size_id);
                      const qty = showPrintModal.type === 'incoming'
                        ? (item.received_quantity || item.expected_quantity)
                        : item.quantity;
                      return (
                        <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                            {item.product?.name || 'Unknown'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 font-mono">
                            {item.product?.sku || '-'}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {colorName ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-xs font-medium text-gray-700 dark:text-gray-300">
                                {colorName}
                              </span>
                            ) : (
                              <span className="text-gray-400 dark:text-gray-500">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {sizeName ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-xs font-medium text-blue-600 dark:text-blue-400">
                                {sizeName}
                              </span>
                            ) : (
                              <span className="text-gray-400 dark:text-gray-500">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900 dark:text-gray-100">
                            {qty}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50 dark:bg-gray-700/50">
                      <td colSpan={4} className="px-4 py-3 text-right text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Total Quantity:
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-gray-900 dark:text-gray-100">
                        {showPrintModal.shipment_items?.reduce((sum: number, item: any) => {
                          return sum + (showPrintModal.type === 'incoming'
                            ? (item.received_quantity || item.expected_quantity || 0)
                            : (item.quantity || 0));
                        }, 0) || 0}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {showPrintModal.notes && (
                <div className="mt-6 bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4">
                  <p className="text-xs text-amber-600 dark:text-amber-400 uppercase tracking-wide mb-1">Notes</p>
                  <p className="text-amber-800 dark:text-amber-200">{showPrintModal.notes}</p>
                </div>
              )}
            </div>

            <div className="border-t dark:border-gray-700 px-6 py-4 bg-gray-50 dark:bg-gray-800/50 flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => setShowPrintModal(null)}
              >
                Close
              </Button>
              <Button
                variant="primary"
                onClick={printShipmentDetails}
                className="flex items-center gap-2"
              >
                <Printer className="h-4 w-4" />
                Print
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
